<template>
  <div class="shared-shiori-view">
    <AppHeader hide-main-nav hide-utility-actions />

    <!-- ローディング -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>しおりを読み込み中...</p>
    </div>

    <!-- エラー -->
    <div v-else-if="error" class="error-container">
      <div class="error-icon">
        <IconSvg name="triangle-exclamation-solid-full" size="xl" />
      </div>
      <h2>{{ error }}</h2>
      <p v-if="errorDetail">{{ errorDetail }}</p>
      <button @click="goToHome" class="btn-home">ホームへ戻る</button>
    </div>

    <!-- しおり表示 -->
    <div v-else-if="shioriData">
      <!-- しおりヘッダー -->
      <div
        class="shiori-header"
        :style="sharedHeaderStyle"
      >
        <div class="container">
          <h1 class="shiori-title">{{ shioriData.shiori.SUBJECT_NMV }}</h1>
          <div class="shiori-meta">
            <span class="meta-item">
              <IconSvg name="calendar-solid-full" size="xs" />
              {{ formatDate(shioriData.shiori.TRIP_FROM) }} ～
              {{ formatDate(shioriData.shiori.TRIP_TO) }}
            </span>
            <span class="meta-item">
              <IconSvg name="user-solid-full" size="xs" />
              {{ shioriData.members.length }}名
            </span>
          </div>

          <!-- 編集権限の案内 -->
          <div class="edit-notice-header">
            <div class="notice-content">
              <IconSvg name="circle-info-solid-full" size="sm" />
              <p>
                {{
                  isTrialMode
                    ? "お試し編集モードです。変更は保存されません"
                    : canEdit
                    ? "このしおりは編集可能です"
                    : "この共有リンクは閲覧専用です"
                }}
              </p>
            </div>
            <button
              v-if="!isTrialMode && !isAuthenticated && !canEdit"
              @click="goToLogin"
              class="btn-login-edit"
            >
              <IconSvg name="arrow-right-from-bracket-solid-full" size="sm" />
              ログイン
            </button>
            <button
              v-else-if="!isTrialMode && canJoinFromLink"
              @click="addShioriToMyPage"
              class="btn-add-shiori"
              :disabled="alreadyOwned"
              :title="
                alreadyOwned
                  ? 'すでにあなたのしおりです'
                  : 'このしおりをマイページに追加'
              "
            >
              <IconSvg
                :name="alreadyOwned ? 'check-solid-full' : 'plus-solid-full'"
                size="sm"
              />
              {{ alreadyOwned ? "追加済み" : "このしおりを追加" }}
            </button>
          </div>
        </div>
      </div>

      <!-- タブラッパー -->
      <div class="tabs-wrapper">
        <div class="container">
          <TabMenu
            :tabs="tabs"
            :activeTab="activeTab"
            @update:activeTab="activeTab = $event"
          />
        </div>
      </div>

      <!-- タブコンテンツ -->
      <div class="tab-content">
        <div class="container">
          <!-- 旅行概要タブ -->
          <div v-if="activeTab === 'overview'" class="overview-tab">
            <div class="overview-layout">
              <div class="overview-main">
                <div class="card mb-4" style="margin-bottom: 1.5rem;">
                  <div class="card-header">
                    <h2 class="card-title">今の予定</h2>
                  </div>
                  <div class="card-body">
                    <p class="text-secondary">今日のスケジュールなどのハイライトがここに表示されます。</p>
                  </div>
                </div>
                <TravelAdvice :shiori-data="shioriData.shiori" />
              </div>
              <div class="overview-side">
                <!-- 既存の旅行概要カード -->
                <div class="card">
                  <div class="card-header">
                    <h2 class="card-title">旅行概要</h2>
                    <div v-if="isTrialMode" class="shiori-action-menu" @click.stop>
                      <button
                        class="shiori-kebab-button"
                        type="button"
                        aria-label="しおりメニュー"
                        @click="isActionMenuOpen = !isActionMenuOpen"
                      >
                        ⋮
                      </button>
                      <div v-if="isActionMenuOpen" class="shiori-action-dropdown">
                        <button
                          class="shiori-action-item"
                          type="button"
                          @click="runTrialAction(openEditModal)"
                        >
                          このしおりを編集
                        </button>
                        <button
                          class="shiori-action-item"
                          type="button"
                          @click="runTrialLoginRequiredAction"
                        >
                          このしおりを共有
                        </button>
                        <button
                          class="shiori-action-item"
                          type="button"
                          @click="runTrialLoginRequiredAction"
                        >
                          このしおりを複製
                        </button>
                        <button
                          class="shiori-action-item"
                          type="button"
                          @click="runTrialLoginRequiredAction"
                        >
                          PDF保存
                        </button>
                        <button
                          class="shiori-action-item shiori-action-delete"
                          type="button"
                          @click="runTrialLoginRequiredAction"
                        >
                          しおりを削除
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="card-body">
                    <div class="info-grid single-col">
                      <div class="info-item">
                        <span class="info-label">しおりタイトル</span>
                        <span class="info-value">{{
                          shioriData.shiori.SUBJECT_NMV
                        }}</span>
                      </div>
                      <div class="info-item">
                        <span class="info-label">旅行日程</span>
                        <span class="info-value">
                          {{ formatDate(shioriData.shiori.TRIP_FROM) }} ～
                          {{ formatDate(shioriData.shiori.TRIP_TO) }}
                        </span>
                      </div>
                      <div class="info-item member-section">
                        <span class="info-label">メンバー</span>
                        <div class="members-list">
                          <div
                            v-for="member in shioriData.members"
                            :key="member.MEMBER_CODE"
                            class="member-card"
                            :title="member.MEMBER_NAME"
                          >
                            <div class="member-avatar-large">
                              <img
                                v-if="
                                  member.PROFILE_IMAGE_BASE64 &&
                                  !hasAvatarError(member.MEMBER_CODE)
                                "
                                :src="formatProfileImageUrl(member.PROFILE_IMAGE_BASE64)"
                                :alt="member.MEMBER_NAME"
                                class="avatar-img"
                                loading="lazy"
                                decoding="async"
                                @error="handleAvatarError(member.MEMBER_CODE)"
                              />
                              <IconSvg
                                v-else
                                name="user-solid-full"
                                size="md"
                                class="avatar-placeholder"
                              />
                            </div>
                          </div>
                          <div
                            v-if="
                              !shioriData.members || shioriData.members.length === 0
                            "
                            class="no-members"
                          >
                            メンバーが登録されていません
                          </div>
                        </div>
                      </div>
                      <div class="info-item">
                        <span class="info-label">渡航先</span>
                        <span class="info-value">
                          <span v-if="formattedDestinations === '-'">-</span>
                          <span
                            v-else-if="
                              shioriData.shiori.DESTINATION_TYPE === 'domestic'
                            "
                          >
                            JP {{ formattedDestinations }}
                          </span>
                          <span v-else>
                            <IconSvg name="earth-americas-solid-full" size="xs" />
                            {{ formattedDestinations }}
                          </span>
                        </span>
                      </div>
                      <div class="info-item">
                        <span class="info-label">費用</span>
                        <span class="info-value">{{
                          totalCost > 0 ? `¥${totalCost.toLocaleString()}` : "-"
                        }}</span>
                      </div>
                      <div v-if="shioriData.shiori.BUDGET" class="info-item">
                        <span class="info-label">予算</span>
                        <span class="info-value budget-breakdown">
                          <span>1人あたり {{ budgetPerPersonDisplay }}</span>
                          <span>全体 {{ budgetTotalDisplay }}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- /旅行概要タブ -->

          <!-- スケジュールタブ -->
          <div v-if="activeTab === 'schedule'">
            <div class="schedule-container">
              <div class="shared-filter-action">
                <button
                  class="shared-filter-toggle"
                  @click="scheduleFilterSidebarOpen = true"
                >
                  <IconSvg name="list-solid-full" size="sm" />
                  フィルター
                  <span v-if="hasScheduleFilters" class="filter-active-dot"></span>
                </button>
                <div
                  v-if="scheduleFilterSidebarOpen"
                  class="shared-filter-overlay"
                  @click="scheduleFilterSidebarOpen = false"
                ></div>
                <aside
                  :class="[
                    'shared-filter-sidebar',
                    { 'is-open': scheduleFilterSidebarOpen },
                  ]"
                >
                  <div class="shared-filter-sidebar-header">
                    <strong>フィルター</strong>
                    <button
                      type="button"
                      aria-label="フィルターを閉じる"
                      @click="scheduleFilterSidebarOpen = false"
                    >
                      ×
                    </button>
                  </div>
                  <div class="shared-filters schedule-filters">
                <label class="shared-filter-group">
                  <span>メンバー</span>
                  <select v-model="scheduleMemberFilter" class="filter-select">
                    <option value="all">全員</option>
                    <option
                      v-for="member in shioriData.members"
                      :key="member.MEMBER_CODE"
                      :value="member.MEMBER_CODE"
                    >
                      {{ member.MEMBER_NAME }}
                    </option>
                  </select>
                </label>
                <label class="shared-filter-group">
                  <span>予定の種類</span>
                  <select v-model="scheduleCategoryFilter" class="filter-select">
                    <option value="all">すべて</option>
                    <option
                      v-for="category in scheduleCategoryOptions"
                      :key="category.value"
                      :value="category.value"
                    >
                      {{ category.label }}
                    </option>
                  </select>
                </label>
                <label class="shared-filter-group">
                  <span>旅程への表示</span>
                  <select v-model="scheduleNeedFilter" class="filter-select">
                    <option value="all">すべて</option>
                    <option value="needed">旅程に含む</option>
                    <option value="not-needed">未確定</option>
                  </select>
                </label>
                <label class="shared-filter-group">
                  <span>予約状況</span>
                  <select v-model="scheduleReservationFilter" class="filter-select">
                    <option value="all">すべて</option>
                    <option value="done">予約完了</option>
                    <option value="pending">予約未完了</option>
                    <option value="none">予約不要</option>
                  </select>
                </label>
                <label class="shared-filter-group">
                  <span>費用</span>
                  <select v-model="scheduleCostFilter" class="filter-select">
                    <option value="all">すべて</option>
                    <option value="with-cost">費用あり</option>
                    <option value="no-cost">費用なし</option>
                  </select>
                </label>
                <button
                  v-if="hasScheduleFilters"
                  class="filter-reset-button"
                  @click="resetScheduleFilters"
                >
                  リセット
                </button>
                  </div>
                </aside>
              </div>
              <div class="shared-schedule-layout">
                <section class="shared-schedule-list-pane">
                  <!-- 日程タブ -->
                  <div class="schedule-tabs">
                    <div class="tabs-header">
                      <button
                        v-for="(day, index) in groupedSchedules"
                        :key="index"
                        :class="['tab-button', { active: activeDay === index }]"
                        @click="activeDay = index"
                      >
                        {{ `${index + 1}日目` }}
                        <span v-if="day.items.length > 0" class="tab-count">{{
                          day.items.length
                        }}</span>
                      </button>
                    </div>

                    <!-- スケジュール一覧 -->
                    <div class="tabs-content">
                      <div
                        v-for="(day, index) in groupedSchedules"
                        :key="index"
                        v-show="activeDay === index"
                        class="day-content"
                      >
                        <div class="day-header-bar">
                          <h3 class="day-title">{{ day.date }}</h3>
                          <button
                            v-if="canEdit"
                            @click="openCreateModalForDate(day.dateObj)"
                            class="btn btn-primary btn-sm"
                          >
                            <IconSvg name="plus-solid-full" size="sm" />
                            スケジュール追加
                          </button>
                        </div>

                        <div v-if="day.items.length === 0" class="no-items">
                          この日のスケジュールはまだありません
                        </div>
                        <div v-else class="schedule-items">
                      <div
                        v-for="item in day.items"
                        :key="`${item.LINE_NON}-${item.DISPLAY_DATE || item.START_YMD}`"
                        :data-schedule-id="item.LINE_NON"
                        class="schedule-item"
                      >
                        <div class="item-pin-number">
                          {{ item.MAP_PIN_NUMBER }}
                        </div>
                        <div class="item-icon">
                          <span class="category-icon">
                            <IconSvg
                              :name="getCategoryIcon(item.CATEGORY_CDV)"
                              size="md"
                            />
                          </span>
                        </div>
                        <div class="item-time">
                          <span class="time-badge">
                            {{ getScheduleTimeLabel(item) }}
                          </span>
                        </div>
                        <div class="item-content">
                          <div class="item-title-row">
                            <h4 class="item-title">{{ item.SUBJECT_NMV }}</h4>
                            <div
                              v-if="item.MEMBERS && item.MEMBERS.length > 0"
                              class="members-avatars"
                              aria-label="参加メンバー"
                            >
                              <div
                                v-for="(member, idx) in item.MEMBERS"
                                :key="idx"
                                class="member-avatar"
                                :title="member.MEMBER_NAME"
                              >
                                <img
                                  v-if="
                                    (member.PROFILE_IMAGE_BASE64 ||
                                      getMemberProfileImage(member.MEMBER_CODE)) &&
                                    !hasAvatarError(member.MEMBER_CODE || idx)
                                  "
                                  :src="
                                    formatProfileImageUrl(
                                      member.PROFILE_IMAGE_BASE64 ||
                                        getMemberProfileImage(member.MEMBER_CODE)
                                    )
                                  "
                                  :alt="member.MEMBER_NAME"
                                  class="avatar-img"
                                  loading="lazy"
                                  decoding="async"
                                  @error="
                                    handleAvatarError(member.MEMBER_CODE || idx)
                                  "
                                />
                                <IconSvg
                                  v-else
                                  name="user-solid-full"
                                  size="xs"
                                  class="avatar-placeholder"
                                />
                              </div>
                            </div>
                          </div>
                          <p v-if="item.REMARKS" class="item-description">
                            {{ item.REMARKS }}
                          </p>

                          <div class="item-meta">
                            <span
                              v-if="item.NEED_FLG === '0'"
                              class="meta-tag not-needed-tag"
                            >
                              不要
                            </span>
                            <span
                              v-if="item.RESERVATION_FLG === '1' && item.RESERVATION_DONE_FLG !== '1'"
                              class="meta-tag reservation-pending-tag"
                            >
                              <IconSvg name="check-solid-full" size="xs" />
                              予約未完了
                            </span>
                            <span
                              v-if="item.RESERVATION_FLG === '1' && item.RESERVATION_DONE_FLG === '1'"
                              class="meta-tag reservation-tag"
                            >
                              <IconSvg name="check-solid-full" size="xs" />
                              予約完了
                            </span>
                            <span
                              v-if="item.ADDRESS_NMV"
                              class="meta-tag location-tag"
                              :title="item.ADDRESS_NMV"
                            >
                              <IconSvg
                                name="location-dot-solid-full"
                                size="xs"
                              />
                              {{ item.ADDRESS_NMV }}
                            </span>
                            <span v-if="item.COST" class="meta-tag cost-tag">
                              <IconSvg name="yen-sign-solid-full" size="xs" />
                              ¥{{ formatCost(item.COST) }}
                            </span>
                            <a
                              v-if="getMapUrl(item)"
                              :href="getMapUrl(item)"
                              target="_blank"
                              rel="noopener noreferrer"
                              class="meta-tag map-link"
                            >
                              <IconSvg name="map-solid-full" size="xs" />
                              地図を開く
                            </a>
                          </div>

                          <!-- アクションボタン -->
                          <div class="item-footer">
                            <!-- 編集可能な場合のみ表示 -->
                            <div v-if="canEdit" class="item-actions">
                              <button
                                @click="editSchedule(item)"
                                class="action-btn edit-btn"
                                title="編集"
                              >
                                <IconSvg
                                  name="pen-to-square-solid-full"
                                  size="sm"
                                />
                              </button>
                              <button
                                @click="deleteSchedule(item.LINE_NON)"
                                class="action-btn delete-btn"
                                title="削除"
                              >
                                <IconSvg name="trash-solid-full" size="sm" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <aside class="shared-schedule-map-pane">
                  <section class="shared-schedule-map-section">
                    <div class="shared-schedule-map-header">
                      <h3 class="shared-schedule-map-title">
                        <IconSvg name="map-solid-full" size="sm" />
                        {{ activeDayMapTitle }}
                      </h3>
                      <span class="shared-schedule-map-count">
                        {{ activeDayItemsWithCoordinates.length }}件
                      </span>
                    </div>
                    <div class="shared-schedule-map-body">
                      <MapComponent
                        :key="`shared-schedule-map-${activeDay}`"
                        :items="activeDayItemsWithCoordinates"
                        :center="activeDayMapCenter"
                        :zoom="activeDayMapZoom"
                      />
                      <p
                        v-if="activeDayItemsWithCoordinates.length === 0"
                        class="shared-schedule-map-empty"
                      >
                        この日付には地図に表示できる場所がありません。
                      </p>
                    </div>
                  </section>
                </aside>
              </div>
            </div>
          </div>
          <!-- /スケジュールタブ -->

          <!-- 持ち物タブ -->
          <div v-if="activeTab === 'belongings'">
            <ChecklistView v-if="isTrialMode" item-type="BELONGINGS" />
            <div v-else class="checklist-container">
              <div class="checklist-header-bar">
                <h3 class="section-title">
                  <IconSvg name="bag-shopping-solid-full" size="md" /> 持ち物
                </h3>
              </div>
              <div v-if="belongingsItems.length > 0" class="checklist-list">
                <div
                  v-for="(items, category) in groupedBelongings"
                  :key="category"
                  class="checklist-category"
                >
                  <h4 class="category-title">{{ category || "未分類" }}</h4>
                  <div
                    v-for="item in items"
                    :key="`${item.PAGE_NO || 'page'}-${item.CHECKLIST_NO}`"
                    class="checklist-item"
                    :class="{ completed: item.COMPLETED_FLG === '1' }"
                  >
                    <div class="checklist-item-left">
                      <input
                        type="checkbox"
                        :checked="item.COMPLETED_FLG === '1'"
                        :disabled="!canEdit"
                        @change="onToggleCheck(item)"
                        class="checklist-checkbox"
                      />
                      <div class="checklist-content">
                        <div class="checklist-title-row">
                          <span class="checklist-name">{{ item.ITEM_NAME }}</span>
                          <span
                            v-if="item.PRIORITY_LEVEL"
                            class="priority-badge"
                            :class="getPriorityClass(item.PRIORITY_LEVEL)"
                          >
                            {{ getPriorityLabel(item.PRIORITY_LEVEL) }}
                          </span>
                        </div>
                        <span v-if="item.DEADLINE" class="checklist-deadline">
                          <IconSvg name="calendar-solid-full" size="xs" />
                          {{ formatDate(item.DEADLINE) }}
                        </span>
                        <p v-if="item.MEMO_TEXT" class="checklist-memo">
                          {{ item.MEMO_TEXT }}
                        </p>
                        <div
                          v-if="item.MEMBERS && item.MEMBERS.length > 0"
                          class="members-avatars"
                          aria-label="担当メンバー"
                        >
                          <div
                            v-for="(member, idx) in item.MEMBERS"
                            :key="member.MEMBER_CODE || member.ASSIGNEE_USER_CODE || idx"
                            class="member-avatar"
                            :title="member.MEMBER_NAME"
                          >
                            <img
                              v-if="
                                (member.PROFILE_IMAGE || member.PROFILE_IMAGE_BASE64 ||
                                  getMemberProfileImage(member.MEMBER_CODE || member.ASSIGNEE_USER_CODE)) &&
                                !hasAvatarError(member.MEMBER_CODE || member.ASSIGNEE_USER_CODE || idx)
                              "
                              :src="
                                formatProfileImageUrl(
                                  member.PROFILE_IMAGE || member.PROFILE_IMAGE_BASE64 ||
                                    getMemberProfileImage(member.MEMBER_CODE || member.ASSIGNEE_USER_CODE)
                                )
                              "
                              :alt="member.MEMBER_NAME"
                              class="avatar-img"
                              loading="lazy"
                              decoding="async"
                              @error="
                                handleAvatarError(member.MEMBER_CODE || member.ASSIGNEE_USER_CODE || idx)
                              "
                            />
                            <IconSvg
                              v-else
                              name="user-solid-full"
                              size="xs"
                              class="avatar-placeholder"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-if="canEdit" class="checklist-item-actions">
                      <button
                        @click="openCopyChecklistModal(item)"
                        class="action-btn copy-btn"
                        title="コピー"
                      >
                        <IconSvg name="copy-solid-full" size="sm" />
                      </button>
                      <button
                        @click="openEditChecklistModal(item)"
                        class="action-btn edit-btn"
                        title="編集"
                      >
                        <IconSvg name="pen-to-square-solid-full" size="sm" />
                      </button>
                      <button
                        @click="onDeleteItem(item)"
                        class="action-btn delete-btn"
                        title="削除"
                      >
                        <IconSvg name="trash-solid-full" size="sm" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="empty-state">
                <p>持ち物はまだありません</p>
              </div>
            </div>
          </div>
          <!-- /持ち物タブ -->

          <!-- To Doタブ -->
          <div v-if="activeTab === 'todo'">
            <ChecklistView v-if="isTrialMode" item-type="TODO" />
            <div v-else class="checklist-container">
              <div class="checklist-header-bar">
                <h3 class="section-title">
                  <IconSvg name="check-solid-full" size="md" /> To Do
                </h3>
              </div>
              <div v-if="todoItems.length > 0" class="checklist-list">
                <div
                  v-for="(items, category) in groupedTodos"
                  :key="category"
                  class="checklist-category"
                >
                  <h4 class="category-title">{{ category || "未分類" }}</h4>
                  <div
                    v-for="item in items"
                    :key="`${item.PAGE_NO || 'page'}-${item.CHECKLIST_NO}`"
                    class="checklist-item"
                    :class="{ completed: item.COMPLETED_FLG === '1' }"
                  >
                    <div class="checklist-item-left">
                      <input
                        type="checkbox"
                        :checked="item.COMPLETED_FLG === '1'"
                        :disabled="!canEdit"
                        @change="onToggleCheck(item)"
                        class="checklist-checkbox"
                      />
                      <div class="checklist-content">
                        <div class="checklist-title-row">
                          <span class="checklist-name">{{ item.ITEM_NAME }}</span>
                          <span
                            v-if="item.PRIORITY_LEVEL"
                            class="priority-badge"
                            :class="getPriorityClass(item.PRIORITY_LEVEL)"
                          >
                            {{ getPriorityLabel(item.PRIORITY_LEVEL) }}
                          </span>
                        </div>
                        <span v-if="item.DEADLINE" class="checklist-deadline">
                          <IconSvg name="calendar-solid-full" size="xs" />
                          {{ formatDate(item.DEADLINE) }}
                        </span>
                        <p v-if="item.MEMO_TEXT" class="checklist-memo">
                          {{ item.MEMO_TEXT }}
                        </p>
                        <div
                          v-if="item.MEMBERS && item.MEMBERS.length > 0"
                          class="members-avatars"
                          aria-label="担当メンバー"
                        >
                          <div
                            v-for="(member, idx) in item.MEMBERS"
                            :key="member.MEMBER_CODE || member.ASSIGNEE_USER_CODE || idx"
                            class="member-avatar"
                            :title="member.MEMBER_NAME"
                          >
                            <img
                              v-if="
                                (member.PROFILE_IMAGE || member.PROFILE_IMAGE_BASE64 ||
                                  getMemberProfileImage(member.MEMBER_CODE || member.ASSIGNEE_USER_CODE)) &&
                                !hasAvatarError(member.MEMBER_CODE || member.ASSIGNEE_USER_CODE || idx)
                              "
                              :src="
                                formatProfileImageUrl(
                                  member.PROFILE_IMAGE || member.PROFILE_IMAGE_BASE64 ||
                                    getMemberProfileImage(member.MEMBER_CODE || member.ASSIGNEE_USER_CODE)
                                )
                              "
                              :alt="member.MEMBER_NAME"
                              class="avatar-img"
                              loading="lazy"
                              decoding="async"
                              @error="
                                handleAvatarError(member.MEMBER_CODE || member.ASSIGNEE_USER_CODE || idx)
                              "
                            />
                            <IconSvg
                              v-else
                              name="user-solid-full"
                              size="xs"
                              class="avatar-placeholder"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-if="canEdit" class="checklist-item-actions">
                      <button
                        @click="openCopyChecklistModal(item)"
                        class="action-btn copy-btn"
                        title="コピー"
                      >
                        <IconSvg name="copy-solid-full" size="sm" />
                      </button>
                      <button
                        @click="openEditChecklistModal(item)"
                        class="action-btn edit-btn"
                        title="編集"
                      >
                        <IconSvg name="pen-to-square-solid-full" size="sm" />
                      </button>
                      <button
                        @click="onDeleteItem(item)"
                        class="action-btn delete-btn"
                        title="削除"
                      >
                        <IconSvg name="trash-solid-full" size="sm" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="empty-state">
                <p>To Doはまだありません</p>
              </div>
            </div>
          </div>
          <!-- /To Doタブ -->

          <!-- 行きたいとこタブ -->
          <div v-if="isTrialMode && activeTab === 'wishlist'">
            <WishlistView />
          </div>
          <!-- /行きたいとこタブ -->

          <!-- 割り勘タブ -->
          <div v-if="activeTab === 'warikan'" class="warikan-tab-content">
            <div class="warikan-container">
              <!-- メンバーなし -->
              <div
                v-if="!shioriData.members || shioriData.members.length === 0"
                class="empty-state"
              >
                <div class="empty-icon">
                  <IconSvg name="yen-sign-solid-full" size="xl" />
                </div>
                <h3 class="empty-title">メンバー情報がありません</h3>
                <p class="empty-description">
                  しおりにメンバーを追加してください
                </p>
              </div>

              <div v-else class="warikan-content">
                <!-- 統計情報 -->
                <div class="stats-panel">
                  <div class="stats-panel-header">
                    <div class="stats-panel-title">
                      <h2 class="section-title">
                        <IconSvg name="yen-sign-solid-full" size="md" />
                        統計情報
                      </h2>
                      <p class="stats-panel-summary">
                        現在 {{ formatCurrency(totalExpenseAmount) }}
                        <span v-if="hasBudget">
                          / 予算比 {{ budgetUsagePercent }}%
                        </span>
                      </p>
                    </div>
                    <button
                      type="button"
                      class="stats-toggle-button"
                      :aria-expanded="isStatsOpen"
                      @click="isStatsOpen = !isStatsOpen"
                    >
                      {{ isStatsOpen ? "閉じる" : "開く" }}
                    </button>
                  </div>

                  <div v-show="isStatsOpen" class="budget-section">
                    <div class="budget-summary-card">
                      <div class="budget-summary-header">
                        <h3 class="budget-card-title">予算と支出</h3>
                        <span
                          class="budget-status"
                          :class="{ over: isOverBudget, unset: !hasBudget }"
                        >
                          {{ budgetStatusLabel }}
                        </span>
                      </div>

                      <div class="budget-metrics">
                        <div class="budget-metric">
                          <span class="metric-label">現在の金額</span>
                          <strong>{{ formatCurrency(totalExpenseAmount) }}</strong>
                        </div>
                        <div class="budget-metric">
                          <span class="metric-label">元の予算</span>
                          <strong>{{
                            hasBudget ? formatCurrency(originalBudget) : "-"
                          }}</strong>
                        </div>
                        <div class="budget-metric">
                          <span class="metric-label">予算比</span>
                          <strong>{{
                            hasBudget ? `${budgetUsagePercent}%` : "-"
                          }}</strong>
                        </div>
                        <div class="budget-metric">
                          <span class="metric-label">{{
                            isOverBudget ? "予算超過" : "残り予算"
                          }}</span>
                          <strong :class="{ danger: isOverBudget }">
                            {{
                              hasBudget
                                ? formatCurrency(Math.abs(remainingBudget))
                                : "-"
                            }}
                          </strong>
                        </div>
                      </div>

                      <div class="budget-progress" aria-hidden="true">
                        <div
                          class="budget-progress-fill"
                          :class="{ over: isOverBudget }"
                          :style="{ width: budgetProgressWidth }"
                        ></div>
                      </div>
                    </div>

                    <div class="expense-chart-card">
                      <div
                        class="expense-donut"
                        :style="expenseDonutStyle"
                        aria-hidden="true"
                      >
                        <div class="expense-donut-center">
                          <span>合計</span>
                          <strong>{{ formatCurrency(totalExpenseAmount) }}</strong>
                        </div>
                      </div>

                      <div class="expense-breakdown">
                        <div
                          v-for="item in expenseBreakdown"
                          :key="item.key"
                          class="expense-breakdown-row"
                        >
                          <div class="breakdown-label-row">
                            <span
                              class="breakdown-color"
                              :style="{ backgroundColor: item.color }"
                            ></span>
                            <span class="breakdown-label">{{ item.label }}</span>
                            <strong>{{ formatCurrency(item.amount) }}</strong>
                          </div>
                          <div class="breakdown-bar" aria-hidden="true">
                            <div
                              class="breakdown-bar-fill"
                              :style="{
                                width: `${item.percent}%`,
                                backgroundColor: item.color,
                              }"
                            ></div>
                          </div>
                        </div>
                        <p
                          v-if="expenseBreakdown.length === 0"
                          class="no-chart-data"
                        >
                          支出データがまだありません
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- メンバー別サマリー -->
                <div class="summary-section">
                  <h2 class="section-title">
                    <IconSvg name="yen-sign-solid-full" size="md" />
                    メンバー別支出
                  </h2>
                  <div class="wk-member-cards">
                    <div
                      v-for="member in memberSummaries"
                      :key="member.memberCode"
                      class="wk-member-card"
                      :data-testid="`warikan-member-${member.memberCode}`"
                      :class="{
                        'balance-positive': member.balance > 0,
                        'balance-negative': member.balance < 0,
                        'balance-zero': member.balance === 0,
                      }"
                    >
                      <div class="wk-member-header">
                        <div class="wk-member-info">
                          <div class="wk-member-avatar">
                            <img
                              v-if="
                                getMemberProfileImage(member.memberCode) &&
                                !hasAvatarError(member.memberCode)
                              "
                              :src="getMemberProfileImage(member.memberCode)"
                              :alt="member.memberName"
                              class="avatar-img"
                              loading="lazy"
                              decoding="async"
                              @error="handleAvatarError(member.memberCode)"
                            />
                            <IconSvg
                              v-else
                              name="user-solid-full"
                              size="sm"
                              class="avatar-placeholder"
                            />
                          </div>
                          <h3 class="wk-member-name" :title="member.memberName">
                            {{ truncateMemberName(member.memberName) }}
                          </h3>
                        </div>
                        <div
                          class="wk-balance-badge"
                          :class="{
                            positive: member.balance > 0,
                            negative: member.balance < 0,
                          }"
                        >
                          {{ member.balance > 0 ? "+" : ""
                          }}{{ formatCurrency(member.balance) }}
                        </div>
                      </div>
                      <div class="wk-member-details">
                        <div class="balance-status" :class="member.balanceStatus" data-testid="warikan-balance-status">
                          {{ member.balanceStatus === "receive" ? "受け取る" : member.balanceStatus === "pay" ? "支払う" : "過不足なし" }}
                        </div>
                        <div class="wk-detail-row">
                          <span class="wk-detail-label">支払った額:</span>
                          <span class="wk-detail-value paid">{{
                            formatCurrency(member.totalPaid)
                          }}</span>
                        </div>
                        <div class="wk-detail-row">
                          <span class="wk-detail-label">負担すべき額:</span>
                          <span class="wk-detail-value owed">{{
                            formatCurrency(member.totalOwed)
                          }}</span>
                        </div>
                        <div class="wk-detail-row">
                          <span class="wk-detail-label">支払件数:</span>
                          <span class="wk-detail-value" data-testid="warikan-payment-count">{{ member.paymentCount }}件</span>
                        </div>
                        <div class="wk-detail-row">
                          <span class="wk-detail-label">負担明細:</span>
                          <span class="wk-detail-value" data-testid="warikan-burden-count">{{ member.burdenItemCount }}件</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 精算リスト -->
                <div v-if="calculationIssues.length" class="calculation-warning" role="alert" data-testid="warikan-calculation-warning">
                  <h2>精算額を計算できない明細があります</h2>
                  <p>内容を確認してから精算してください。</p>
                  <ul><li v-for="issue in calculationIssues" :key="`${issue.type}-${issue.lineNon}`">{{ issue.message }}</li></ul>
                </div>
                <div v-if="settlements.length > 0" class="settlements-section">
                  <h2 class="section-title">
                    <IconSvg name="recycle-solid-full" size="md" /> 精算方法
                  </h2>
                  <div class="settlement-list">
                    <div
                      v-for="(settlement, index) in settlements"
                      :key="index"
                      class="settlement-item"
                      :class="{ settled: settlement.settled }"
                    >
                      <div class="settlement-content">
                        <span class="from-member" :title="settlement.fromName">
                          {{ truncateMemberName(settlement.fromName) }}
                        </span>
                        <span class="arrow">→</span>
                        <span class="to-member" :title="settlement.toName">
                          {{ truncateMemberName(settlement.toName) }}
                        </span>
                        <span
                          class="status-badge"
                          :class="settlement.settled ? 'paid' : 'unpaid'"
                        >
                          {{ settlement.settled ? "精算済み" : "未支払" }}
                        </span>
                      </div>
                      <div class="settlement-controls">
                        <div class="settlement-amount">
                          {{ formatCurrency(settlement.amount) }}
                        </div>
                        <button
                          v-if="canEdit"
                          class="status-toggle"
                          :class="settlement.settled ? 'status-toggle-undo' : 'status-toggle-complete'"
                          :disabled="updatingSettlementKey === settlement.key"
                          @click="toggleSharedSettlement(settlement)"
                        >
                          {{ settlement.settled ? "未支払に戻す" : "精算済みにする" }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="calculationIssues.length === 0 && settlements.length === 0" class="no-settlements" data-testid="warikan-no-settlements">
                  <div class="success-icon">
                    <IconSvg name="check-solid-full" size="xl" />
                  </div>
                  <h3>精算の必要はありません</h3>
                  <p>全員の支払いと負担額が均等です</p>
                </div>

                <!-- 費用詳細 -->
                <div class="expenses-section">
                  <div class="expenses-heading">
                    <h2 class="section-title">
                      <IconSvg name="list-solid-full" size="md" /> 費用詳細
                    </h2>
                    <button
                      v-if="canEdit"
                      class="btn btn-primary shared-warikan-add"
                      @click="openWarikanModal()"
                    >
                      <IconSvg name="plus-solid-full" size="sm" />
                      割り勘を追加
                    </button>
                  </div>
                  <div class="shared-filter-action">
                    <button
                      class="shared-filter-toggle"
                      @click="expenseFilterSidebarOpen = true"
                    >
                      <IconSvg name="list-solid-full" size="sm" />
                      フィルター
                      <span
                        v-if="hasExpenseFilters"
                        class="filter-active-dot"
                      ></span>
                    </button>
                    <div
                      v-if="expenseFilterSidebarOpen"
                      class="shared-filter-overlay"
                      @click="expenseFilterSidebarOpen = false"
                    ></div>
                    <aside
                      :class="[
                        'shared-filter-sidebar',
                        { 'is-open': expenseFilterSidebarOpen },
                      ]"
                    >
                      <div class="shared-filter-sidebar-header">
                        <strong>費用の絞り込み</strong>
                        <button
                          type="button"
                          aria-label="フィルターを閉じる"
                          @click="expenseFilterSidebarOpen = false"
                        >
                          ×
                        </button>
                      </div>
                      <div class="shared-filters expense-filters">
                    <label class="shared-filter-group">
                      <span>種類</span>
                      <select v-model="expenseTypeFilter" class="filter-select">
                        <option value="all">すべての費用</option>
                        <option value="schedule">スケジュール</option>
                        <option value="warikan">割り勘</option>
                      </select>
                    </label>
                    <label class="shared-filter-group">
                      <span>負担者</span>
                      <select v-model="expenseMemberFilter" class="filter-select">
                        <option value="all">すべての負担者</option>
                        <option
                          v-for="member in shioriData.members"
                          :key="member.MEMBER_CODE"
                          :value="member.MEMBER_CODE"
                        >
                          {{ member.MEMBER_NAME }}
                        </option>
                      </select>
                    </label>
                    <label class="shared-filter-group">
                      <span>精算状態</span>
                      <select v-model="expenseSettlementFilter" class="filter-select">
                        <option value="all">すべて</option>
                        <option value="unsettled">未精算</option>
                        <option value="settled">精算済み</option>
                      </select>
                    </label>
                    <button
                      v-if="hasExpenseFilters"
                      class="filter-reset-button"
                      @click="resetExpenseFilters"
                    >
                      リセット
                    </button>
                      </div>
                    </aside>
                  </div>
                  <div v-if="filteredExpenses.length === 0" class="no-expenses">
                    <p>条件に一致する費用データがありません</p>
                  </div>
                  <div v-else class="expense-list">
                    <!-- スケジュール費用 -->
                    <div
                      v-for="expense in filteredScheduleExpenses"
                      :key="'schedule-' + expense.lineNon"
                      class="expense-item"
                    >
                      <div class="expense-header">
                        <div class="expense-title">
                          <span class="expense-icon"
                            ><IconSvg
                              :name="getCategoryIcon(expense.category)"
                              size="sm"
                          /></span>
                          <span>{{ expense.title }}</span>
                          <span class="expense-badge schedule"
                            >スケジュール</span
                          >
                          <span
                            class="expense-badge"
                            :class="expense.settled ? 'settled' : 'unsettled'"
                          >
                            {{ expense.settled ? "精算済み" : "未精算" }}
                          </span>
                        </div>
                        <div class="expense-cost">
                          {{ formatExpenseDisplay(expense) }}
                        </div>
                      </div>
                      <div class="expense-details">
                        <div class="expense-info">
                          <span class="expense-info-label">日時:</span>
                          <span>{{ expense.date }} {{ expense.time }}</span>
                        </div>
                        <div class="expense-info">
                          <span class="expense-info-label">支払者:</span>
                          <span class="payer-chip" :title="expense.paidByName">
                            <span class="payer-icon" aria-hidden="true">
                              <IconSvg name="yen-sign-solid-full" size="xs" />
                            </span>
                            <span class="payer-name">
                              {{ truncateMemberName(expense.paidByName) }}
                            </span>
                          </span>
                        </div>
                        <div class="expense-info">
                          <span class="expense-info-label">参加者:</span>
                          <span :title="expense.members.join(', ')">
                            {{ formatMemberNameList(expense.members) }}
                          </span>
                        </div>
                        <div class="expense-info">
                          <span class="expense-info-label">1人あたり:</span>
                          <span>{{ formatCurrency(expense.perPerson) }}</span>
                        </div>
                      </div>
                      <button
                        v-if="canEdit"
                        class="expense-settlement-button"
                        :class="expense.settled ? 'settlement-undo' : 'settlement-complete'"
                        :disabled="updatingExpenseKey === `schedule-${expense.lineNon}`"
                        @click="toggleSharedExpenseSettlement(expense)"
                      >
                        {{ expense.settled ? "未精算に戻す" : "精算済みにする" }}
                      </button>
                    </div>

                    <!-- 独立した割り勘 -->
                    <div
                      v-for="expense in filteredIndependentWarikans"
                      :key="'warikan-' + expense.lineNon"
                      class="expense-item warikan-item"
                    >
                      <div class="expense-header">
                        <div class="expense-title">
                          <span class="expense-icon"
                            ><IconSvg name="bag-shopping-solid-full" size="sm"
                          /></span>
                          <span>{{ expense.title }}</span>
                          <span class="expense-badge independent">割り勘</span>
                          <span
                            class="expense-badge"
                            :class="expense.settled ? 'settled' : 'unsettled'"
                          >
                            {{ expense.settled ? "精算済み" : "未精算" }}
                          </span>
                        </div>
                        <div class="expense-cost">
                          {{ formatExpenseDisplay(expense) }}
                        </div>
                      </div>
                      <div class="expense-details">
                        <div class="expense-info">
                          <span class="expense-info-label">通貨:</span>
                          <span>{{ expense.currency }}</span>
                        </div>
                        <div class="expense-info">
                          <span class="expense-info-label">支払者:</span>
                          <span class="payer-chip" :title="expense.paidByName">
                            <span class="payer-icon" aria-hidden="true">
                              <IconSvg name="yen-sign-solid-full" size="xs" />
                            </span>
                            <span class="payer-name">
                              {{ truncateMemberName(expense.paidByName) }}
                            </span>
                          </span>
                        </div>
                        <div class="expense-info">
                          <span class="expense-info-label">参加者:</span>
                          <span :title="expense.members.join(', ')">
                            {{ formatMemberNameList(expense.members) }}
                          </span>
                        </div>
                        <div class="expense-info">
                          <span class="expense-info-label">1人あたり:</span>
                          <span>{{ formatCurrency(expense.perPerson) }}</span>
                        </div>
                      </div>
                      <button
                        v-if="canEdit"
                        class="expense-settlement-button"
                        :class="expense.settled ? 'settlement-undo' : 'settlement-complete'"
                        :disabled="updatingExpenseKey === `warikan-${expense.lineNon}`"
                        @click="toggleSharedExpenseSettlement(expense)"
                      >
                        {{ expense.settled ? "未精算に戻す" : "精算済みにする" }}
                      </button>
                      <div v-if="canEdit" class="shared-warikan-actions">
                        <button
                          class="action-btn edit-btn"
                          title="編集"
                          @click="openWarikanModal(expense)"
                        >
                          <IconSvg name="pen-to-square-solid-full" size="sm" />
                        </button>
                        <button
                          class="action-btn delete-btn"
                          title="削除"
                          @click="deleteSharedWarikan(expense)"
                        >
                          <IconSvg name="trash-solid-full" size="sm" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- /割り勘タブ -->
        </div>
      </div>

      <!-- フローティングボタン（編集可能な場合のみ、主要4タブで表示） -->
      <button
        v-if="canEdit && ['schedule', 'belongings', 'todo', 'warikan'].includes(activeTab)"
        @click="handleFabClick"
        class="floating-btn"
        :title="getFabTitle(activeTab)"
      >
        <span class="floating-btn-icon">
          <IconSvg name="plus-solid-full" size="lg" />
        </span>
      </button>

      <!-- スケジュール作成モーダル -->
      <CreateScheduleModal
        v-if="canEdit"
        v-model="showModal"
        :shioriId="shioriData.shiori.SHIORI_NOV"
        :editMode="editMode"
        :scheduleData="editingSchedule"
        :initialDate="initialDate"
        :members="shioriData.members"
        :shareToken="route.params.token"
        @create="onScheduleCreated"
        @update="onScheduleUpdated"
        @access-denied="handleSharedWriteFailure"
      />
      <CreateWarikanModal
        v-if="canEdit"
        v-model="showWarikanModal"
        :shioriId="shioriData.shiori.SHIORI_NOV"
        :editMode="warikanEditMode"
        :warikanData="editingWarikan"
        :members="shioriData.members"
        :shareToken="route.params.token"
        @create="onWarikanSaved"
        @update="onWarikanSaved"
        @access-denied="handleSharedWriteFailure"
      />
      <ManageShioriModal
        v-if="isTrialMode"
        v-model="isEditModalOpen"
        :shiori-data="shioriData.shiori"
        :can-delete="false"
        :can-delete-members="true"
        @updated="handleTrialShioriUpdated"
      />

      <!-- 共有チェックリスト作成/編集モーダル -->
      <CreateChecklistModal
        v-if="canEdit"
        :is-open="showChecklistModal"
        :shiori-id="shioriData?.shiori?.SHIORI_NOV || shioriData?.SHIORI_ID || ''"
        :edit-mode="checklistEditMode"
        :copy-mode="checklistCopyMode"
        :checklist-data="editingChecklist"
        :item-type="checklistModalType"
        :share-token="route.params.token || ''"
        :members="shioriData?.members || []"
        @close="showChecklistModal = false"
        @create="handleChecklistCreated"
        @update="handleChecklistUpdated"
        @access-denied="handleSharedWriteFailure"
      />
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useShioriStore } from "@/store/shiori-store";
import { useAuthStore } from "@/store/authStore";
import { useScheduleStore } from "@/store/schedule-store";
import { useScheduleCategoryStore } from "@/store/schedule-category-store";
import { useWarikanStore } from "@/store/warikan-store";
import { useChecklistStore } from "@/store/checklist-store";
import { useNotificationStore } from "@/store/notification-store";
import AppHeader from "@/components/AppHeader.vue";
import TabMenu from "@/components/TabMenu.vue";
import CreateScheduleModal from "@/components/CreateScheduleModal.vue";
import CreateWarikanModal from "@/components/CreateWarikanModal.vue";
import CreateChecklistModal from "@/components/CreateChecklistModal.vue";
import ChecklistView from "@/components/ChecklistSection.vue";
import ManageShioriModal from "@/components/ManageShioriModal.vue";
import IconSvg from "@/components/IconSvg.vue";
import MapComponent from "@/components/MapComponent.vue";
import TravelAdvice from "@/components/TravelAdvice.vue";
import WishlistView from "@/views/WishlistView.vue";
import { API_URL } from "@/config/api.js";
import {
  fetchSharedShioriImageUrl,
  resolveEmbeddedShioriImageUrl,
  revokeShioriImageUrl,
} from "@/utils/shiori-image.js";
import {
  fetchGuestChecklists,
  fetchGuestExchangeRates,
  fetchGuestMembers,
  fetchGuestSchedules,
  fetchGuestShiori,
  fetchGuestWarikanMembers,
  fetchGuestWarikans,
} from "@/utils/guest-session";
import {
  buildExchangeRateMap,
  formatCurrencyAmount,
  formatExpenseAmount,
  formatJpy,
  normalizeCurrency,
  toJpy,
} from "@/utils/exchange-rate";
import { calculateWarikan } from "@/utils/warikan-calculation";

export default {
  name: "SharedShioriView",
  components: {
    AppHeader,
    TabMenu,
    CreateScheduleModal,
    CreateWarikanModal,
    CreateChecklistModal,
    ChecklistView,
    ManageShioriModal,
    IconSvg,
    MapComponent,
    TravelAdvice,
    WishlistView,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const shioriStore = useShioriStore();
    const authStore = useAuthStore();
    const scheduleStore = useScheduleStore();
    const scheduleCategoryStore = useScheduleCategoryStore();
    const warikanStore = useWarikanStore();
    const checklistStore = useChecklistStore();
    const notificationStore = useNotificationStore();

    const loading = ref(true);
    const error = ref("");
    const errorDetail = ref("");
    const shioriData = ref(null);
    const resolveInitialTab = (tab) =>
      ["todo", "warikan"].includes(tab) ? tab : "overview";
    const activeTab = ref(resolveInitialTab(route.meta.sharedInitialTab));
    const activeDay = ref(0);
    const showModal = ref(false);
    const editMode = ref(false);
    const editingSchedule = ref(null);
    const initialDate = ref(null);
    const showWarikanModal = ref(false);
    const warikanEditMode = ref(false);
    const editingWarikan = ref(null);
    const isEditModalOpen = ref(false);
    const scheduleMemberFilter = ref("all");
    const scheduleCategoryFilter = ref("all");
    const scheduleNeedFilter = ref("all");
    const scheduleReservationFilter = ref("all");
    const scheduleCostFilter = ref("all");
    const scheduleFilterSidebarOpen = ref(false);
    const expenseTypeFilter = ref("all");
    const expenseMemberFilter = ref("all");
    const expenseSettlementFilter = ref("all");
    const expenseFilterSidebarOpen = ref(false);
    const updatingSettlementKey = ref("");
    const updatingExpenseKey = ref("");
    const isTrialMode = computed(() => route.name === "Trial");
    const headerImageSource = computed(() => {
      const shiori = shioriData.value?.shiori;
      const embeddedImageUrl = resolveEmbeddedShioriImageUrl(shiori);
      if (embeddedImageUrl) return { embeddedImageUrl };
      if (shiori?.HAS_IMAGE && shiori.SHIORI_NOV && route.params.token) {
        return {
          shioriId: shiori.SHIORI_NOV,
          shareToken: route.params.token,
        };
      }
      return null;
    });
    const loadedHeaderImageUrl = ref("");
    watch(
      headerImageSource,
      async (source, _previousSource, onCleanup) => {
        loadedHeaderImageUrl.value = "";
        let active = true;
        let objectUrl = "";

        onCleanup(() => {
          active = false;
          revokeShioriImageUrl(objectUrl);
        });

        if (!source) return;
        if (source.embeddedImageUrl) {
          const image = new Image();
          image.onload = () => {
            if (active) loadedHeaderImageUrl.value = source.embeddedImageUrl;
          };
          image.onerror = () => {
            if (active) loadedHeaderImageUrl.value = "";
          };
          image.src = source.embeddedImageUrl;
          return;
        }

        try {
          objectUrl = await fetchSharedShioriImageUrl(
            source.shioriId,
            source.shareToken,
          );
          if (active) loadedHeaderImageUrl.value = objectUrl;
          else revokeShioriImageUrl(objectUrl);
        } catch {
          if (active) loadedHeaderImageUrl.value = "";
        }
      },
      { immediate: true },
    );
    const sharedHeaderStyle = computed(() => {
      return loadedHeaderImageUrl.value
        ? {
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.65), rgba(0, 0, 0, 0.65)), url(${loadedHeaderImageUrl.value})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }
        : {};
    });
    const isActionMenuOpen = ref(false);
    const isStatsOpen = ref(true);
    const syncedEditableShareTokens = new Set();

    const tabs = computed(() => [
      { id: "overview", label: "旅行概要", icon: "list-solid-full" },
      { id: "schedule", label: "スケジュール", icon: "calendar-solid-full" },
      { id: "warikan", label: "割り勘", icon: "yen-sign-solid-full" },
      ...(isTrialMode.value
        ? [{ id: "wishlist", label: "行きたいとこ", icon: "map-solid-full" }]
        : []),
      { id: "belongings", label: "持ち物", icon: "bag-shopping-solid-full" },
      { id: "todo", label: "To Do", icon: "check-solid-full" },
    ]);

    watch(
      () => route.meta.sharedInitialTab,
      (initialTab) => {
        activeTab.value = resolveInitialTab(initialTab);
      },
    );

    const isAuthenticated = computed(() => !!authStore.token);
    const alreadyOwned = computed(() => {
      if (!isAuthenticated.value || !shioriData.value) return false;
      const myCode = authStore.user?.APP_USER_CODE || authStore.user?.user_id;
      const currentShioriId = shioriData.value.shiori.SHIORI_NOV;
      // 自分がオーナー
      if (shioriData.value.shiori.ENTRY_USER === myCode) return true;
      // すでに共有参加済み（sharedShioriに元のshioriIdで存在する）
      return shioriStore.sharedShiori.some(
        (s) => s.SHIORI_NOV === currentShioriId,
      );
    });
    const canEdit = computed(() => {
      if (isTrialMode.value) return true;
      return shioriData.value?.can_edit === true;
    });
    const canJoinFromLink = computed(() => {
      if (isTrialMode.value) return false;
      const purpose =
        shioriData.value?.purpose || shioriData.value?.token_purpose;
      return purpose !== "shiori_share";
    });

    const exchangeRateMap = computed(() =>
      buildExchangeRateMap(shioriData.value?.exchange_rates || []),
    );

    const formattedDestinations = computed(() => {
      const value = shioriData.value?.shiori?.DESTINATIONS;
      if (!value) return "-";
      try {
        const destinations = Array.isArray(value) ? value : JSON.parse(value);
        const visible = destinations
          .map((destination) => String(destination || "").trim())
          .filter(Boolean);
        return visible.length ? visible.join("、") : "-";
      } catch {
        return String(value).trim() || "-";
      }
    });

    const totalCost = computed(() => {
      if (!shioriData.value?.schedules) return 0;
      return shioriData.value.schedules.reduce((sum, schedule) => {
        return sum + (schedule.COST || 0);
      }, 0);
    });

    const budgetMemberCount = computed(() =>
      Math.max(shioriData.value?.members?.length || 0, 1),
    );

    const budgetTotalDisplay = computed(() =>
      formatCurrency(shioriData.value?.shiori?.BUDGET || 0),
    );

    const budgetPerPersonDisplay = computed(() => {
      const budget = Number(shioriData.value?.shiori?.BUDGET || 0);
      if (!budget) return "-";
      return formatCurrency(budget / budgetMemberCount.value);
    });

    const schedulesWithCost = computed(() => {
      if (!shioriData.value?.schedules) return [];
      return shioriData.value.schedules.filter(
        (s) => s.COST && Number(s.COST) > 0,
      );
    });

    const reservedSchedules = computed(() => {
      if (!shioriData.value?.schedules) return [];
      return shioriData.value.schedules.filter(
        (s) => s.RESERVATION_FLG === "1",
      );
    });

    const categoryLabels = {
      car: "車",
      train: "電車",
      plane: "飛行機",
      bus: "バス",
      ship: "船",
      hotel: "宿泊",
      restaurant: "食事",
      entertainment: "娯楽",
      activity: "アクティビティ",
      sightseeing: "観光",
      shopping: "買い物",
      nature: "自然",
      movement: "移動",
      other: "その他",
    };

    const scheduleCategoryOptions = computed(() => {
      const categories = new Set(
        (shioriData.value?.schedules || [])
          .map((schedule) => schedule.CATEGORY_CDV)
          .filter(Boolean),
      );
      return [...categories].map((value) => ({
        value,
        label:
          scheduleCategoryStore.cdvToName[value] ||
          categoryLabels[value] ||
          "その他",
      }));
    });

    const filteredSchedules = computed(() =>
      (shioriData.value?.schedules || []).filter((schedule) => {
        const memberMatches =
          scheduleMemberFilter.value === "all" ||
          (schedule.MEMBERS || []).some(
            (member) => member.MEMBER_CODE === scheduleMemberFilter.value,
          );
        const categoryMatches =
          scheduleCategoryFilter.value === "all" ||
          schedule.CATEGORY_CDV === scheduleCategoryFilter.value;
        const needMatches =
          scheduleNeedFilter.value === "all" ||
          (scheduleNeedFilter.value === "needed"
            ? schedule.NEED_FLG !== "0"
            : schedule.NEED_FLG === "0");
        const reservationMatches =
          scheduleReservationFilter.value === "all" ||
          (scheduleReservationFilter.value === "done"
            ? schedule.RESERVATION_FLG === "1" &&
              schedule.RESERVATION_DONE_FLG === "1"
            : scheduleReservationFilter.value === "pending"
              ? schedule.RESERVATION_FLG === "1" &&
                schedule.RESERVATION_DONE_FLG !== "1"
              : schedule.RESERVATION_FLG !== "1");
        const hasCost = Number(schedule.COST || 0) > 0;
        const costMatches =
          scheduleCostFilter.value === "all" ||
          (scheduleCostFilter.value === "with-cost" ? hasCost : !hasCost);
        return (
          memberMatches &&
          categoryMatches &&
          needMatches &&
          reservationMatches &&
          costMatches
        );
      }),
    );

    const hasScheduleFilters = computed(
      () =>
        scheduleMemberFilter.value !== "all" ||
        scheduleCategoryFilter.value !== "all" ||
        scheduleNeedFilter.value !== "all" ||
        scheduleReservationFilter.value !== "all" ||
        scheduleCostFilter.value !== "all",
    );

    const resetScheduleFilters = () => {
      scheduleMemberFilter.value = "all";
      scheduleCategoryFilter.value = "all";
      scheduleNeedFilter.value = "all";
      scheduleReservationFilter.value = "all";
      scheduleCostFilter.value = "all";
    };

    const checklistItems = computed(() => shioriData.value?.checklists || []);

    const normalizeChecklistType = (item) =>
      String(item.ITEM_TYPE || "TODO").toUpperCase();

    const belongingsItems = computed(() =>
      checklistItems.value.filter(
        (item) => normalizeChecklistType(item) === "BELONGINGS",
      ),
    );

    const todoItems = computed(() =>
      checklistItems.value.filter(
        (item) => normalizeChecklistType(item) !== "BELONGINGS",
      ),
    );

    const groupChecklistItems = (items) => {
      const grouped = {};
      items.forEach((item) => {
        const category = item.CATEGORY_NAME || "未分類";
        if (!grouped[category]) {
          grouped[category] = [];
        }
        grouped[category].push(item);
      });
      return grouped;
    };

    const groupedBelongings = computed(() =>
      groupChecklistItems(belongingsItems.value),
    );

    const groupedTodos = computed(() => groupChecklistItems(todoItems.value));

    const scheduleExpenses = computed(() => {
      if (!shioriData.value?.schedules) return [];
      return shioriData.value.schedules
        .filter((s) => s.COST && Number(s.COST) > 0)
        .map((s) => {
          const memberNames =
            s.MEMBERS?.filter((m) => m.SPLIT_PARTICIPATE_FLG === "1").map(
              (m) => m.MEMBER_NAME,
            ) || [];
          const paidByMember = s.MEMBERS?.find(
            (m) => m.PAID_AMOUNT && Number(m.PAID_AMOUNT) > 0,
          );
          const participantCount = memberNames.length || 1;
          const memberCodes =
            s.MEMBERS?.filter(
              (m) => m.SPLIT_PARTICIPATE_FLG === "1",
            ).map((m) => m.MEMBER_CODE) || [];
          const originalCurrency = normalizeCurrency(s.CURRENCY);
          const converted = toJpy(
            Number(s.COST),
            originalCurrency,
            exchangeRateMap.value,
          );
          const costJpy = converted.amount;

          return {
            lineNon: s.LINE_NON,
            title: s.SUBJECT_NMV,
            category: s.CATEGORY_CDV,
            date: s.START_YMD,
            time: s.START_TIME || "",
            cost: costJpy,
            originalCost: Number(s.COST),
            originalCurrency,
            currency: originalCurrency,
            costJpy,
            missingRate: converted.missingRate,
            paidByName: paidByMember?.MEMBER_NAME || "不明",
            paidByCode: paidByMember?.MEMBER_CODE || null,
            members: memberNames,
            memberCodes,
            perPerson: costJpy / participantCount,
            settled: s.EXPENSE_SETTLED_FLG === "1",
            type: "schedule",
          };
        });
    });

    const independentWarikans = computed(() => {
      if (!shioriData.value?.warikan) return [];
      const warikans = shioriData.value.warikan;
      const warikanMembers = shioriData.value.warikan_members || [];
      return warikans.map((w) => {
        const participantCodes = warikanMembers
          .filter((wm) => wm.LINE_NON === w.LINE_NON)
          .map((wm) => wm.BENEFICIARY_USER_CODE);
        const memberNames = participantCodes
          .map((code) => {
            const m = shioriData.value.members.find(
              (m) => m.MEMBER_CODE === code,
            );
            return m?.MEMBER_NAME || "";
          })
          .filter((n) => n);
        const payer = shioriData.value.members.find(
          (m) => m.MEMBER_CODE === w.PAYER_USER_CODE || m.APP_USER_CODE === w.PAYER_USER_CODE,
        );
        const count = memberNames.length || 1;
        const originalCurrency = normalizeCurrency(w.CURRENCY);
        const converted = toJpy(
          Number(w.AMOUNT),
          originalCurrency,
          exchangeRateMap.value,
        );
        const costJpy = converted.amount;
        return {
          lineNon: w.LINE_NON,
          title: w.SUBJECT || w.SUBJECT_NMV || "割り勘",
          cost: costJpy,
          originalCost: Number(w.AMOUNT),
          originalCurrency,
          currency: originalCurrency,
          costJpy,
          missingRate: converted.missingRate,
          paidByName: payer?.MEMBER_NAME || "不明",
          paidByCode: w.PAYER_USER_CODE,
          members: memberNames,
          memberCodes: participantCodes,
          perPerson: costJpy / count,
          settled: w.EXPENSE_SETTLED_FLG === "1",
          type: "warikan",
          rawData: { ...w, MEMBERS: participantCodes },
        };
      });
    });

    const allExpenses = computed(() => {
      return [...scheduleExpenses.value, ...independentWarikans.value];
    });

    const originalBudget = computed(() =>
      Math.round(Number(shioriData.value?.shiori?.BUDGET || 0)),
    );

    const hasBudget = computed(() => originalBudget.value > 0);

    const totalExpenseAmount = computed(() =>
      allExpenses.value.reduce(
        (sum, expense) => sum + (Number(expense.cost) || 0),
        0,
      ),
    );

    const budgetUsagePercent = computed(() => {
      if (!hasBudget.value) return 0;
      return Math.round((totalExpenseAmount.value / originalBudget.value) * 100);
    });

    const budgetProgressWidth = computed(() =>
      hasBudget.value ? `${Math.min(budgetUsagePercent.value, 100)}%` : "0%",
    );

    const remainingBudget = computed(
      () => originalBudget.value - totalExpenseAmount.value,
    );

    const isOverBudget = computed(
      () => hasBudget.value && remainingBudget.value < 0,
    );

    const budgetStatusLabel = computed(() => {
      if (!hasBudget.value) return "予算未設定";
      return isOverBudget.value ? "予算超過" : "予算内";
    });

    const expenseChartColors = [
      "#22c55e",
      "#4ade80",
      "#86efac",
      "#bbf7d0",
      "#84cc16",
      "#a3e635",
      "#bef264",
      "#d9f99d",
    ];

    const categoryLabelFallback = {
      restaurant: "食事",
      hotel: "宿泊",
      transport: "交通",
      movement: "移動",
      activity: "観光・体験",
      sightseeing: "観光",
      shopping: "買い物",
      other: "その他",
      warikan: "割り勘",
    };

    const getExpenseCategoryKey = (expense) => {
      if (expense.type === "warikan") return "warikan";
      return expense.category || "other";
    };

    const getExpenseCategoryLabel = (key) => {
      return (
        scheduleCategoryStore.cdvToName[key] ||
        categoryLabelFallback[key] ||
        "その他"
      );
    };

    const expenseBreakdown = computed(() => {
      const total = totalExpenseAmount.value;
      const grouped = allExpenses.value.reduce((acc, expense) => {
        const key = getExpenseCategoryKey(expense);
        acc[key] = (acc[key] || 0) + (Number(expense.cost) || 0);
        return acc;
      }, {});

      return Object.entries(grouped)
        .map(([key, amount], index) => ({
          key,
          label: getExpenseCategoryLabel(key),
          amount,
          percent: total ? Math.round((amount / total) * 100) : 0,
          color: expenseChartColors[index % expenseChartColors.length],
        }))
        .filter((item) => item.amount > 0)
        .sort((a, b) => b.amount - a.amount);
    });

    const expenseDonutStyle = computed(() => {
      if (expenseBreakdown.value.length === 0) {
        return { background: "#e5e7eb" };
      }

      let current = 0;
      const segments = expenseBreakdown.value.map((item) => {
        const start = current;
        current +=
          totalExpenseAmount.value > 0
            ? (item.amount / totalExpenseAmount.value) * 360
            : 0;
        return `${item.color} ${start}deg ${current}deg`;
      });

      return { background: `conic-gradient(${segments.join(", ")})` };
    });

    const filteredExpenses = computed(() =>
      allExpenses.value.filter((expense) => {
        const typeMatches =
          expenseTypeFilter.value === "all" ||
          expense.type === expenseTypeFilter.value;
        const memberMatches =
          expenseMemberFilter.value === "all" ||
          expense.memberCodes.includes(expenseMemberFilter.value);
        const settlementMatches =
          expenseSettlementFilter.value === "all" ||
          (expenseSettlementFilter.value === "settled"
            ? expense.settled
            : !expense.settled);
        return typeMatches && memberMatches && settlementMatches;
      }),
    );

    const filteredScheduleExpenses = computed(() =>
      filteredExpenses.value.filter((expense) => expense.type === "schedule"),
    );

    const filteredIndependentWarikans = computed(() =>
      filteredExpenses.value.filter((expense) => expense.type === "warikan"),
    );

    const hasExpenseFilters = computed(
      () =>
        expenseTypeFilter.value !== "all" ||
        expenseMemberFilter.value !== "all" ||
        expenseSettlementFilter.value !== "all",
    );

    const resetExpenseFilters = () => {
      expenseTypeFilter.value = "all";
      expenseMemberFilter.value = "all";
      expenseSettlementFilter.value = "all";
    };

    const warikanCalculation = computed(() =>
      calculateWarikan({
        members: shioriData.value?.members || [],
        expenses: allExpenses.value,
        settlementStatuses: shioriData.value?.warikan_settlement_statuses || [],
      }),
    );
    const memberSummaries = computed(() => warikanCalculation.value.memberSummaries);
    const settlements = computed(() => warikanCalculation.value.settlements);
    const calculationIssues = computed(() => warikanCalculation.value.issues);

    const avatarErrorMap = reactive({});
    const handleAvatarError = (code) => {
      if (code) {
        avatarErrorMap[code] = true;
      }
    };
    const hasAvatarError = (code) => {
      return !!(code && avatarErrorMap[code]);
    };

    const formatProfileImageUrl = (url) => {
      if (!url) return null;
      if (url.startsWith("data:")) {
        return url;
      }
      const cleanBase = API_URL.endsWith("/") ? API_URL.slice(0, -1) : API_URL;
      let fullUrl = url;
      if (url.startsWith("http://") || url.startsWith("https://")) {
        fullUrl = url;
      } else if (url.startsWith("/api/")) {
        fullUrl = `${cleanBase}${url.slice(4)}`;
      } else if (url.startsWith("/")) {
        fullUrl = `${cleanBase}${url}`;
      } else {
        fullUrl = `${cleanBase}/${url}`;
      }
      if (fullUrl.includes("/profile-image") && !fullUrl.includes("?")) {
        fullUrl = `${fullUrl}?t=${Date.now()}`;
      }
      return fullUrl;
    };

    const getMemberProfileImage = (id) => {
      if (!id || !shioriData.value?.members) return null;
      const member = shioriData.value.members.find(
        (m) =>
          m.MEMBER_CODE === id ||
          m.APP_USER_CODE === id ||
          (m.MEMBER_NAME && m.MEMBER_NAME.trim() === String(id).trim()),
      );
      if (!member) return null;
      return formatProfileImageUrl(
        member?.PROFILE_IMAGE_BASE64 || member?.PROFILE_IMAGE || null,
      );
    };

    const toDateKey = (value) => {
      if (!value) return "";
      if (value instanceof Date) {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, "0");
        const day = String(value.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      }
      const [datePart] = String(value).split("T");
      return datePart;
    };

    const dateKeyToLocalDate = (dateKey) => {
      const [year, month, day] = dateKey.split("-").map(Number);
      return new Date(year, month - 1, day);
    };

    const generateTripDates = () => {
      if (!shioriData.value?.shiori) return [];

      const tripFromKey = toDateKey(shioriData.value.shiori.TRIP_FROM);
      const tripToKey = toDateKey(shioriData.value.shiori.TRIP_TO);
      const scheduleDateKeys = (shioriData.value.schedules || []).flatMap(
        (schedule) => [
          toDateKey(schedule.START_YMD),
          toDateKey(schedule.END_YMD || schedule.START_YMD),
        ],
      );
      const minKey = [tripFromKey, ...scheduleDateKeys]
        .filter(Boolean)
        .sort()[0];
      const sortedEndKeys = [tripToKey, ...scheduleDateKeys]
        .filter(Boolean)
        .sort();
      const maxKey = sortedEndKeys[sortedEndKeys.length - 1];

      if (!minKey || !maxKey) return [];

      const dates = [];
      const currentDate = dateKeyToLocalDate(minKey);
      const tripTo = dateKeyToLocalDate(maxKey);

      while (currentDate <= tripTo) {
        dates.push(toDateKey(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }

      return dates;
    };

    const groupedSchedules = computed(() => {
      const allDates = generateTripDates();
      const schedules = filteredSchedules.value;

      const scheduleOccursOn = (schedule, dateKey) => {
        const start = toDateKey(schedule.START_YMD);
        const end = toDateKey(schedule.END_YMD || start);
        return Boolean(start && start <= dateKey && dateKey <= end);
      };

      const scheduleForDate = (schedule, dateKey) => {
        const start = toDateKey(schedule.START_YMD);
        const end = toDateKey(schedule.END_YMD || start);
        const isFirstDay = start === dateKey;
        const isLastDay = end === dateKey;
        const isMiddleDay = !isFirstDay && !isLastDay;

        return {
          ...schedule,
          DISPLAY_DATE: dateKey,
          DISPLAY_START_TIME: isFirstDay ? schedule.START_TIME : "00:00",
          DISPLAY_END_TIME: isLastDay ? schedule.END_TIME : "23:59",
          IS_MULTI_DAY_CONTINUATION: !isFirstDay,
          IS_MULTI_DAY_MIDDLE: isMiddleDay,
        };
      };

      return allDates.map((dateStr) => {
        const daySchedules = schedules
          .filter((schedule) => scheduleOccursOn(schedule, dateStr))
          .map((schedule) => scheduleForDate(schedule, dateStr));

        daySchedules.sort((a, b) => {
          const timeA = a.DISPLAY_START_TIME || "00:00";
          const timeB = b.DISPLAY_START_TIME || "00:00";
          if (timeA !== timeB) return timeA.localeCompare(timeB);
          const endTimeA = a.DISPLAY_END_TIME || "00:00";
          const endTimeB = b.DISPLAY_END_TIME || "00:00";
          return endTimeA.localeCompare(endTimeB);
        });

        return {
          date: formatScheduleDate(dateStr),
          dateObj: dateStr,
          items: daySchedules.map((schedule, scheduleIndex) => ({
            ...schedule,
            MAP_PIN_NUMBER: scheduleIndex + 1,
          })),
        };
      });
    });

    const activeDayGroup = computed(
      () => groupedSchedules.value[activeDay.value] || null,
    );

    const activeDayItemsWithCoordinates = computed(() =>
      getItemsWithCoordinates(activeDayGroup.value?.items || []),
    );

    const activeDayMapCenter = computed(() =>
      getMapCenter(activeDayGroup.value?.items || []),
    );

    const activeDayMapZoom = computed(() =>
      getMapZoom(activeDayGroup.value?.items || []),
    );

    const activeDayMapTitle = computed(() => {
      const dayNumber = activeDay.value + 1;
      return `${dayNumber}日目の訪問先マップ`;
    });

    const fetchSharedShiori = async () => {
      if (isTrialMode.value) {
        const shioriId = route.params.id;
        const shiori = fetchGuestShiori().find(
          (item) => item.SHIORI_NOV === shioriId,
        );
        if (!shiori) {
          error.value = "お試ししおりが見つかりません";
          errorDetail.value =
            "もう一度お試しページからしおりを作成してください。";
          shioriData.value = null;
          loading.value = false;
          return false;
        }
        shioriData.value = {
          can_edit: true,
          shiori,
          members: fetchGuestMembers(shioriId),
          schedules: fetchGuestSchedules(shioriId),
          checklists: fetchGuestChecklists(shioriId),
          warikan: fetchGuestWarikans(shioriId),
          warikan_members: fetchGuestWarikanMembers(shioriId),
          warikan_settlement_statuses: [],
          exchange_rates: fetchGuestExchangeRates(shioriId),
        };
        error.value = "";
        errorDetail.value = "";
        loading.value = false;
        return true;
      }

      const token = route.params.token;

      if (!token) {
        error.value = "無効な共有リンクです";
        errorDetail.value =
          "この共有リンクは無効か、有効期限が切れています。";
        shioriData.value = null;
        loading.value = false;
        return false;
      }

      try {
        loading.value = true;
        const data = await shioriStore.getSharedShiori(token);
        console.log("📦 取得した共有しおりデータ:", data);
        shioriData.value = data;
        error.value = "";
        errorDetail.value = "";
        await syncEditableShareAccess(token, data);
        return true;
      } catch (err) {
        console.error("共有しおり取得エラー:", err);
        if ([403, 404].includes(err.response?.status)) {
          shioriData.value = null;
          error.value = "共有リンクを利用できません";
          errorDetail.value =
            "この共有リンクは無効か、有効期限が切れています。";
        } else {
          error.value = "しおりの読み込みに失敗しました";
          errorDetail.value = "ネットワーク接続を確認してください。";
        }
        return false;
      } finally {
        loading.value = false;
      }
    };

    const syncEditableShareAccess = async (token, data) => {
      const purpose = data?.purpose || data?.token_purpose;
      if (
        !isAuthenticated.value ||
        data?.can_edit !== true ||
        purpose !== "shiori_share" ||
        syncedEditableShareTokens.has(token)
      ) {
        return;
      }

      syncedEditableShareTokens.add(token);
      try {
        await shioriStore.joinShiori(token);
      } catch (joinError) {
        console.error("共有しおり編集権限同期エラー:", joinError);
      }
    };

    const closeSharedEditModals = () => {
      showModal.value = false;
      showWarikanModal.value = false;
      showChecklistModal.value = false;
      editingSchedule.value = null;
      editingWarikan.value = null;
      editingChecklist.value = null;
    };

    const handleSharedWriteFailure = async (writeError, fallbackMessage) => {
      if (![403, 404].includes(writeError?.response?.status)) {
        alert(
          fallbackMessage ||
            writeError?.response?.data?.detail ||
            "変更を保存できませんでした",
        );
        return false;
      }

      const linkIsValid = await fetchSharedShiori();
      if (!linkIsValid || !canEdit.value) {
        closeSharedEditModals();
        if (linkIsValid) {
          notificationStore.showWarning(
            "この共有リンクでは編集できなくなりました。",
          );
        }
        return true;
      }

      alert(
        fallbackMessage ||
          writeError?.response?.data?.detail ||
          "変更を保存できませんでした",
      );
      return false;
    };

    const openWarikanModal = (expense = null) => {
      warikanEditMode.value = Boolean(expense);
      editingWarikan.value = expense?.rawData || null;
      showWarikanModal.value = true;
    };

    const onWarikanSaved = async () => {
      showWarikanModal.value = false;
      await fetchSharedShiori();
    };

    const deleteSharedWarikan = async (expense) => {
      if (isTrialMode.value) {
        showTrialRestriction();
        return;
      }
      if (!confirm(`「${expense.title}」を削除しますか？`)) return;
      try {
        await warikanStore.deleteSharedWarikan(
          route.params.token,
          expense.lineNon,
        );
        await fetchSharedShiori();
      } catch (err) {
        console.error("共有リンク割り勘削除エラー:", err);
        await handleSharedWriteFailure(err, "割り勘の削除に失敗しました");
      }
    };

    const toggleSharedSettlement = async (settlement) => {
      if (isTrialMode.value) {
        showTrialRestriction();
        return;
      }
      updatingSettlementKey.value = settlement.key;
      try {
        await warikanStore.updateSharedSettlementStatus(
          route.params.token,
          settlement,
          !settlement.settled,
        );
        await fetchSharedShiori();
      } catch (err) {
        console.error("共有リンク精算ステータス更新エラー:", err);
        await handleSharedWriteFailure(err, "精算状態を更新できませんでした");
      } finally {
        updatingSettlementKey.value = "";
      }
    };

    const toggleSharedExpenseSettlement = async (expense) => {
      if (isTrialMode.value) {
        showTrialRestriction();
        return;
      }
      if (
        !expense.settled &&
        !confirm(`「${expense.title}」を精算済みにしますか？`)
      ) {
        return;
      }
      updatingExpenseKey.value = `${expense.type}-${expense.lineNon}`;
      try {
        await warikanStore.updateSharedExpenseSettlementStatus(
          route.params.token,
          expense,
          !expense.settled,
        );
        await fetchSharedShiori();
      } catch (err) {
        console.error("共有リンク費用精算状態更新エラー:", err);
        await handleSharedWriteFailure(err, "費用の精算状態を更新できませんでした");
      } finally {
        updatingExpenseKey.value = "";
      }
    };

    const formatDateRange = (from, to) => {
      if (!from || !to) return "";
      const fromDate = new Date(from);
      const toDate = new Date(to);
      const diffTime = Math.abs(toDate - fromDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return `${formatDate(from)} ～ ${formatDate(to)} (${diffDays}日間)`;
    };

    const formatDate = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const weekdays = ["日", "月", "火", "水", "木", "金", "土"];
      const weekday = weekdays[date.getDay()];
      return `${year}年${String(month).padStart(2, "0")}月${String(
        day,
      ).padStart(2, "0")}日(${weekday})`;
    };

    const formatScheduleDate = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const weekdays = ["日", "月", "火", "水", "木", "金", "土"];
      return `${month}/${day}(${weekdays[date.getDay()]})`;
    };

    const formatCurrency = (amount) => {
      return formatJpy(amount);
    };

    const formatExpenseDisplay = (expense) => {
      if (!expense) return formatJpy(0);
      if (expense.missingRate) {
        return `${formatCurrencyAmount(
          expense.originalCost,
          expense.originalCurrency,
        )} / JPY -`;
      }
      return formatExpenseAmount(
        expense.originalCost,
        expense.originalCurrency,
        exchangeRateMap.value,
      );
    };

    const formatCost = (cost) => {
      if (!cost) return "0";
      return new Intl.NumberFormat("ja-JP").format(cost);
    };

    const getMapUrl = (item) => {
      const latitude = Number(item.LATITUDE);
      const longitude = Number(item.LONGITUDE);
      const hasCoordinates =
        item.LATITUDE !== null &&
        item.LATITUDE !== undefined &&
        item.LATITUDE !== "" &&
        item.LONGITUDE !== null &&
        item.LONGITUDE !== undefined &&
        item.LONGITUDE !== "";
      if (
        hasCoordinates &&
        Number.isFinite(latitude) &&
        Number.isFinite(longitude)
      ) {
        return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
      }
      if (item.ADDRESS_NMV) {
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          item.ADDRESS_NMV,
        )}`;
      }
      return null;
    };

    const hasCoordinates = (item) => {
      const latitude = Number(item?.LATITUDE);
      const longitude = Number(item?.LONGITUDE);
      return (
        item?.LATITUDE !== null &&
        item?.LATITUDE !== undefined &&
        item?.LATITUDE !== "" &&
        item?.LONGITUDE !== null &&
        item?.LONGITUDE !== undefined &&
        item?.LONGITUDE !== "" &&
        Number.isFinite(latitude) &&
        Number.isFinite(longitude)
      );
    };

    const getItemsWithCoordinates = (items) => items.filter(hasCoordinates);

    const getMapCenter = (items) => {
      const targets = getItemsWithCoordinates(items);
      if (targets.length === 0) return [35.6762, 139.6503];
      const avgLat =
        targets.reduce((sum, item) => sum + Number(item.LATITUDE), 0) /
        targets.length;
      const avgLng =
        targets.reduce((sum, item) => sum + Number(item.LONGITUDE), 0) /
        targets.length;
      return [avgLat, avgLng];
    };

    const getMapZoom = (items) =>
      getItemsWithCoordinates(items).length > 1 ? 12 : 14;

    const truncateMemberName = (name) => {
      const characters = Array.from(name || "");
      return characters.length > 5
        ? `${characters.slice(0, 5).join("")}...`
        : name;
    };

    const formatMemberNameList = (names) =>
      (names || []).map((name) => truncateMemberName(name)).join(", ");

    const getScheduleTimeLabel = (item) => {
      const start = item.DISPLAY_START_TIME || item.START_TIME || "--:--";
      const displayStart = start === "00:00" ? "0:00" : start;
      return displayStart;
    };

    const getCategoryIcon = (category) => {
      const iconMap = {
        car: "car-solid",
        train: "train-subway-solid-full",
        plane: "plane-solid-full",
        bus: "bus-solid-full",
        ship: "sailboat-solid-full",
        hotel: "bed-solid-full",
        restaurant: "utensils-solid-full",
        entertainment: "clapperboard-solid-full",
        activity: "person-walking-solid-full",
        sightseeing: "building-columns-solid-full",
        shopping: "bag-shopping-solid-full",
        nature: "tree-solid-full",
        other: "map-solid-full",
      };
      return iconMap[category] || "map-solid-full";
    };

    const showTrialRestriction = () => {
      goToLogin();
    };

    const refreshTrialData = async () => {
      if (isTrialMode.value) {
        await fetchSharedShiori();
      }
    };

    const openEditModal = () => {
      isEditModalOpen.value = true;
    };

    const handleTrialShioriUpdated = async () => {
      isEditModalOpen.value = false;
      await refreshTrialData();
    };

    const runTrialAction = (action) => {
      isActionMenuOpen.value = false;
      action();
    };

    const runTrialLoginRequiredAction = () => {
      isActionMenuOpen.value = false;
      notificationStore.showInfo("ログイン後の機能です");
    };

    const getMemberName = (userCode) => {
      if (!userCode || !shioriData.value?.members) return "不明";
      // APP_USER_CODEで検索
      const member = shioriData.value.members.find(
        (m) => m.APP_USER_CODE === userCode,
      );
      if (member) return member.MEMBER_NAME;

      // MEMBER_CODEで検索（フォールバック）
      const memberByCode = shioriData.value.members.find(
        (m) => m.MEMBER_CODE === userCode,
      );
      return memberByCode ? memberByCode.MEMBER_NAME : userCode;
    };

    const openCreateModal = () => {
      editMode.value = false;
      editingSchedule.value = null;
      initialDate.value = null;
      showModal.value = true;
    };

    const openCreateModalForDate = (date) => {
      editMode.value = false;
      editingSchedule.value = null;
      initialDate.value = date;
      showModal.value = true;
    };

    const editSchedule = (schedule) => {
      editMode.value = true;
      editingSchedule.value = schedule;
      showModal.value = true;
    };

    const deleteSchedule = async (lineNon) => {
      if (!confirm("このスケジュールを削除しますか？")) return;

      try {
        await scheduleStore.deleteSchedule(
          shioriData.value.shiori.SHIORI_NOV,
          lineNon,
          route.params.token,
        );
        // データを再取得
        await fetchSharedShiori();
      } catch (err) {
        console.error("スケジュール削除エラー:", err);
        await handleSharedWriteFailure(
          err,
          "スケジュールの削除に失敗しました",
        );
      }
    };

    const onScheduleCreated = async () => {
      await fetchSharedShiori();
      showModal.value = false;
    };

    const onScheduleUpdated = async () => {
      await fetchSharedShiori();
      showModal.value = false;
    };

    const goToLogin = () => {
      if (isTrialMode.value) {
        router.push({
          path: "/login",
          query: { redirect: `/trial/${route.params.id}` },
        });
        return;
      }
      router.push({
        path: "/login",
        query: { redirect: `/shared/${route.params.token}` },
      });
    };

    const goToRegister = () => {
      router.push("/login");
    };

    const goToHome = () => {
      router.push("/");
    };

    const addShioriToMyPage = async () => {
      try {
        const token = route.params.token;
        const result = await shioriStore.joinShiori(token);
        // 元のしおりへ遷移
        await router.push(`/shiori/${result.shiori_id}`);
      } catch (err) {
        console.error("しおり追加エラー:", err);
        alert("しおりへの参加に失敗しました。");
      }
    };

    onMounted(async () => {
      if (isTrialMode.value) {
        await fetchSharedShiori();
        return;
      }

      await Promise.all([
        fetchSharedShiori(),
        scheduleCategoryStore.fetchCategories(),
      ]);
      if (
        isAuthenticated.value &&
        route.query.join === "1"
      ) {
        await addShioriToMyPage();
        return;
      }
      if (isAuthenticated.value) {
        try {
          await shioriStore.fetchSharedWithMe();
        } catch (e) {
          // 共有しおりの取得失敗は無視（追加済みチェックにのみ使用）
        }
      }
    });

    const showChecklistModal = ref(false);
    const checklistModalType = ref("BELONGINGS");
    const checklistEditMode = ref(false);
    const checklistCopyMode = ref(false);
    const editingChecklist = ref(null);

    const openChecklistModal = (type = "BELONGINGS") => {
      if (!canEdit.value) return;
      if (isTrialMode.value) {
        showTrialRestriction();
        return;
      }
      checklistModalType.value = type;
      checklistEditMode.value = false;
      checklistCopyMode.value = false;
      editingChecklist.value = null;
      showChecklistModal.value = true;
    };

    const openEditChecklistModal = (item) => {
      if (!canEdit.value) return;
      if (isTrialMode.value) {
        showTrialRestriction();
        return;
      }
      checklistModalType.value = String(item.ITEM_TYPE || "TODO").toUpperCase();
      checklistEditMode.value = true;
      checklistCopyMode.value = false;
      editingChecklist.value = item;
      showChecklistModal.value = true;
    };

    const openCopyChecklistModal = (item) => {
      if (!canEdit.value) return;
      if (isTrialMode.value) {
        showTrialRestriction();
        return;
      }
      checklistModalType.value = String(item.ITEM_TYPE || "TODO").toUpperCase();
      checklistEditMode.value = false;
      checklistCopyMode.value = true;
      editingChecklist.value = item;
      showChecklistModal.value = true;
    };

    const getPriorityClass = (level) => {
      return `priority-${level?.toLowerCase() || "normal"}`;
    };

    const getPriorityLabel = (level) => {
      const labels = {
        HIGH: "高",
        NORMAL: "通常",
        LOW: "低",
      };
      return labels[level] || "通常";
    };

    const handleChecklistCreated = async () => {
      showChecklistModal.value = false;
      await fetchSharedShiori();
    };

    const handleChecklistUpdated = async () => {
      showChecklistModal.value = false;
      await fetchSharedShiori();
    };

    const onToggleCheck = async (item) => {
      if (!canEdit.value) return;
      if (isTrialMode.value) {
        showTrialRestriction();
        return;
      }
      const token = route.params.token;
      if (!token) return;
      try {
        const newStatus = item.COMPLETED_FLG === "1" ? "0" : "1";
        await checklistStore.updateSharedChecklist(
          token,
          item.PAGE_NO || "1",
          item.CHECKLIST_NO,
          { completedFlg: newStatus },
        );
        await fetchSharedShiori();
      } catch (err) {
        console.error("共有チェックリスト更新エラー:", err);
        await handleSharedWriteFailure(
          err,
          "チェック状態の更新に失敗しました",
        );
      }
    };

    const onDeleteItem = async (item) => {
      if (!canEdit.value) return;
      if (isTrialMode.value) {
        showTrialRestriction();
        return;
      }
      if (!confirm(`「${item.ITEM_NAME}」を削除してもよろしいですか？`)) return;
      const token = route.params.token;
      if (!token) return;
      try {
        await checklistStore.deleteSharedChecklist(
          token,
          item.PAGE_NO || "1",
          item.CHECKLIST_NO,
        );
        await fetchSharedShiori();
      } catch (err) {
        console.error("共有チェックリスト削除エラー:", err);
        await handleSharedWriteFailure(
          err,
          "チェックリストの削除に失敗しました",
        );
      }
    };

    const getFabTitle = (tab) => {
      switch (tab) {
        case "schedule":
          return "スケジュール作成";
        case "belongings":
          return "持ち物追加";
        case "todo":
          return "To Do追加";
        case "warikan":
          return "割り勘支出追加";
        default:
          return "新規追加";
      }
    };

    const handleFabClick = () => {
      if (!canEdit.value) return;
      if (isTrialMode.value) {
        showTrialRestriction();
        return;
      }

      switch (activeTab.value) {
        case "schedule":
          openCreateModal();
          break;
        case "belongings":
          openChecklistModal("BELONGINGS");
          break;
        case "todo":
          openChecklistModal("TODO");
          break;
        case "warikan":
          openWarikanModal();
          break;
      }
    };

    return {
      loading,
      error,
      errorDetail,
      shioriData,
      sharedHeaderStyle,
      activeTab,
      activeDay,
      tabs,
      isTrialMode,
      isActionMenuOpen,
      isStatsOpen,
      isAuthenticated,
      alreadyOwned,
      canEdit,
      canJoinFromLink,
      formattedDestinations,
      formatDateRange,
      formatDate,
      formatCurrency,
      formatExpenseDisplay,
      formatCost,
      getMapUrl,
      hasCoordinates,
      getMapCenter,
      getMapZoom,
      truncateMemberName,
      formatMemberNameList,
      getScheduleTimeLabel,
      getCategoryIcon,
      getMemberName,
      totalCost,
      budgetPerPersonDisplay,
      budgetTotalDisplay,
      schedulesWithCost,
      reservedSchedules,
      scheduleMemberFilter,
      scheduleCategoryFilter,
      scheduleNeedFilter,
      scheduleReservationFilter,
      scheduleCostFilter,
      scheduleFilterSidebarOpen,
      scheduleCategoryOptions,
      hasScheduleFilters,
      resetScheduleFilters,
      belongingsItems,
      todoItems,
      groupedBelongings,
      groupedTodos,
      groupedSchedules,
      activeDayItemsWithCoordinates,
      activeDayMapCenter,
      activeDayMapZoom,
      activeDayMapTitle,
      route,
      showWarikanModal,
      warikanEditMode,
      editingWarikan,
      isEditModalOpen,
      openWarikanModal,
      onWarikanSaved,
      deleteSharedWarikan,
      expenseTypeFilter,
      expenseMemberFilter,
      expenseSettlementFilter,
      expenseFilterSidebarOpen,
      hasExpenseFilters,
      resetExpenseFilters,
      filteredExpenses,
      filteredScheduleExpenses,
      filteredIndependentWarikans,
      updatingSettlementKey,
      updatingExpenseKey,
      toggleSharedSettlement,
      toggleSharedExpenseSettlement,
      memberSummaries,
      independentWarikans,
      allExpenses,
      originalBudget,
      hasBudget,
      totalExpenseAmount,
      budgetUsagePercent,
      budgetProgressWidth,
      remainingBudget,
      isOverBudget,
      budgetStatusLabel,
      expenseBreakdown,
      expenseDonutStyle,
      settlements,
      calculationIssues,
      getMemberProfileImage,
      formatProfileImageUrl,
      handleAvatarError,
      hasAvatarError,
      openEditModal,
      handleTrialShioriUpdated,
      runTrialAction,
      runTrialLoginRequiredAction,
      showModal,
      editMode,
      editingSchedule,
      initialDate,
      openCreateModal,
      openCreateModalForDate,
      editSchedule,
      deleteSchedule,
      onScheduleCreated,
      onScheduleUpdated,
      showChecklistModal,
      checklistEditMode,
      checklistCopyMode,
      editingChecklist,
      checklistModalType,
      openChecklistModal,
      openEditChecklistModal,
      openCopyChecklistModal,
      getPriorityClass,
      getPriorityLabel,
      handleChecklistCreated,
      handleChecklistUpdated,
      onToggleCheck,
      onDeleteItem,
      getFabTitle,
      handleFabClick,
      handleSharedWriteFailure,
      goToLogin,
      goToRegister,
      goToHome,
      addShioriToMyPage,
      API_URL,
    };
  },
};
</script>

<style scoped>
.shared-shiori-view {
  min-height: 100vh;
  background-color: var(--color-background);
}

.shiori-action-menu {
  position: relative;
  margin-left: auto;
}

.shiori-kebab-button {
  width: 36px;
  height: 36px;
  border: 1px solid var(--color-border);
  border-radius: 0.5rem;
  background: var(--color-surface);
  color: var(--color-text);
  cursor: pointer;
  font-size: 1.25rem;
  line-height: 1;
}

.shiori-action-dropdown {
  position: absolute;
  top: calc(100% + 0.4rem);
  right: 0;
  z-index: 20;
  min-width: 180px;
  padding: 0.35rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 0.5rem;
  box-shadow: 0 16px 36px rgba(31, 41, 55, 0.16);
}

.shiori-action-item {
  width: 100%;
  padding: 0.7rem 0.85rem;
  border: 0;
  border-radius: 0.4rem;
  background: transparent;
  color: var(--color-text);
  cursor: pointer;
  font: inherit;
  font-weight: 700;
  text-align: left;
}

.shiori-action-item:hover {
  background: var(--color-background);
}

.shiori-action-delete {
  color: var(--color-danger);
}

.shiori-action-delete:hover {
  background: #fef2f2;
}

/* しおりヘッダー */
.shiori-header {
  background: linear-gradient(135deg, #2d4a2d 0%, #385238 100%);
  color: white;
  padding: 2rem 1rem;
  position: relative;
  min-height: 200px;
  display: flex;
  align-items: center;
}

.shiori-header .container {
  text-align: left;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
}

/* 背景画像がある場合のオーバーレイ効果は inline style で実装済み */

.back-link {
  display: inline-flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  transition: opacity var(--transition-fast);
}

.back-link:hover {
  opacity: 0.8;
}

.shiori-title {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: white;
  text-align: left;
}

.shiori-meta {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.9);
  justify-content: flex-start;
  margin-bottom: 1rem;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* 編集案内（ヘッダー内） */
.edit-notice-header {
  margin-top: 1rem;
  padding: 1rem;
  background-color: rgba(255, 255, 255, 0.15);
  border-left: 4px solid rgba(255, 255, 255, 0.6);
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  backdrop-filter: blur(5px);
}

.edit-notice-header .notice-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 250px;
}

.edit-notice-header p {
  margin: 0;
  color: white;
  font-weight: 600;
  font-size: 0.95rem;
}

/* タブラッパー */
.tabs-wrapper {
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border-light);
  position: sticky;
  top: 73px;
  z-index: 95;
}

.tabs-wrapper .container {
  max-width: 1200px;
  margin: 0 auto;
}

/* タブコンテンツ */
.tab-content {
  padding: 2rem 1rem;
  background-color: var(--bg-secondary, #f5f5f5);
}

.tab-content .container {
  max-width: 1200px;
  margin: 0 auto;
}

/* 概要タブ（通常のしおり画面と共通の見た目） */
.overview-tab {
  max-width: 900px;
  margin: 0 auto;
}

.overview-tab .card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem var(--spacing-lg);
  margin: calc(-1 * var(--spacing-lg));
  margin-bottom: var(--spacing-lg);
  border-bottom: none;
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  background: linear-gradient(
    135deg,
    var(--shiori-light, #b4cf9e) 0%,
    var(--shiori-lighter, #c5dcb3) 100%
  );
}

.overview-tab .card-title {
  margin: 0;
  color: white;
}

.info-grid {
  display: grid;
  gap: 1.5rem;
}

.info-item {
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 1rem;
  align-items: start;
}

.info-item.member-section {
  align-items: start;
}

.members-list {
  display: flex;
  flex-flow: row wrap;
  gap: 0.5rem;
}

.member-card {
  display: flex;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  transition: all 0.2s;
}

.member-card:hover {
  transform: translateY(-1px);
}

.member-avatar-large {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  background-color: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 2px solid #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.member-avatar-large .avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.member-avatar-large .avatar-placeholder {
  color: var(--color-text-muted);
}

.no-members {
  padding: 2rem;
  text-align: center;
  color: var(--color-text-muted);
  font-size: 0.875rem;
}

.info-label {
  font-weight: 600;
  color: var(--text-secondary, #6b7280);
  font-size: 0.875rem;
}

.info-value {
  color: var(--text-primary, #111827);
  font-size: 0.9375rem;
}

.budget-breakdown {
  display: grid;
  gap: 0.2rem;
}

/* ボタンスタイル */
.btn-login-edit,
.btn-add-shiori {
  padding: 0.6rem 1.25rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
}

.btn-login-edit {
  background-color: var(--color-surface);
  color: #b4cf9e;
}

.btn-login-edit:hover {
  background-color: rgba(255, 255, 255, 0.9);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.btn-add-shiori {
  background-color: rgba(76, 175, 80, 0.9);
  color: white;
}

.btn-add-shiori:hover {
  background-color: #4caf50;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.btn-add-shiori:disabled {
  background-color: var(--color-text-muted);
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-home {
  padding: 0.75rem 2rem;
  background-color: #b4cf9e;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-home:hover {
  background-color: #5568d3;
  transform: translateY(-2px);
}

/* ローディング・エラー表示 */
.loading-container,
.error-container {
  max-width: 600px;
  margin: 4rem auto;
  text-align: center;
  background: var(--color-surface);
  padding: 3rem 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #b4cf9e;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  color: #c62828;
}

.error-container h2 {
  color: #c62828;
  margin-bottom: 0.5rem;
}

.error-container p {
  color: #666;
  margin-bottom: 2rem;
}

/* 古いbtn-loginスタイル（削除予定） */
.btn-login,
.btn-login-small {
  background-color: #b4cf9e;
  color: white;
  border: none;
  padding: 0.5rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-login:hover,
.btn-login-small:hover {
  background-color: #5568d3;
  transform: translateY(-2px);
}

.btn-login-edit,
.btn-add-shiori {
  padding: 0.6rem 1.25rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
}

.btn-login-edit {
  background-color: #b4cf9e;
  color: white;
}

.btn-login-edit:hover {
  background-color: #5568d3;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.btn-add-shiori {
  background-color: #4caf50;
  color: white;
}

.btn-add-shiori:hover {
  background-color: #388e3c;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.loading-container,
.error-container {
  max-width: 600px;
  margin: 4rem auto;
  text-align: center;
  background: var(--color-surface);
  padding: 3rem 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #b4cf9e;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.error-container h2 {
  color: #c62828;
  margin-bottom: 0.5rem;
}

.error-container p {
  color: #666;
  margin-bottom: 2rem;
}

.btn-home {
  padding: 0.75rem 2rem;
  background-color: #b4cf9e;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-home:hover {
  background-color: #5568d3;
  transform: translateY(-2px);
}

.shiori-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
}
-top: 1.5rem;
padding-left: 1.5rem;
padding-right .trip-overview {
  background: var(--color-surface);
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.overview-title {
  margin: 0 0 1rem 0;
  font-size: 2rem;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.overview-content {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 1rem;
}

.overview-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #666;
  font-size: 0.95rem;
}

.overview-icon {
  color: #b4cf9e;
}

.edit-notice {
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: #fff3e0;
  border-left: 4px solid #f57c00;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.edit-notice .notice-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 250px;
}

.edit-notice.edit-available {
  background-color: #e8f5e9;
  border-left-color: #4caf50;
}

.edit-notice p {
  margin: 0;
  color: #e65100;
  font-weight: 600;
  font-size: 0.95rem;
}

.edit-notice.edit-available p {
  color: #2e7d32;
}

.tab-content {
  background: var(--color-surface);
  border-radius: 0 0 16px 16px;
  padding: 1.5rem;
  min-height: 400px;
}

.schedule-container {
  width: 100%;
  --shared-schedule-panel-height: max(420px, calc(100vh - 220px));
}

.shared-schedule-layout {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: stretch;
}

.shared-schedule-list-pane,
.shared-schedule-map-pane {
  min-width: 0;
}

.shared-schedule-map-section {
  position: sticky;
  top: 1rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-md);
  overflow: hidden;
}

.shared-schedule-map-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--color-border-light);
  background: var(--color-background);
}

.shared-schedule-map-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  margin: 0;
  color: var(--color-text);
  font-size: 1rem;
  font-weight: 700;
}

.shared-schedule-map-count {
  flex-shrink: 0;
  padding: 0.25rem 0.625rem;
  border-radius: var(--radius-full);
  background: var(--primary-color);
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
}

.shared-schedule-map-body {
  position: relative;
  min-height: 360px;
}

.shared-schedule-map-body :deep(.map-container),
.shared-schedule-map-body :deep(.map) {
  min-height: 360px;
}

.shared-schedule-map-empty {
  position: absolute;
  left: 1rem;
  right: 1rem;
  bottom: 1rem;
  margin: 0;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  color: var(--color-text-muted);
  font-size: 0.8125rem;
  line-height: 1.5;
}

@media (min-width: 1024px) {
  .shared-schedule-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(420px, 48%);
    align-items: start;
    min-height: 0;
  }

  .shared-schedule-list-pane,
  .shared-schedule-map-pane {
    height: var(--shared-schedule-panel-height);
    min-height: 0;
  }

  .schedule-tabs {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    overflow: hidden;
  }

  .tabs-header {
    flex-shrink: 0;
  }

  .tabs-content {
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  .day-content {
    display: flex;
    height: 100%;
    min-height: 0;
    flex-direction: column;
  }

  .day-header-bar {
    flex-shrink: 0;
  }

  .schedule-items {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding-right: 0.25rem;
  }

  .shared-schedule-map-section {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }

  .shared-schedule-map-body,
  .shared-schedule-map-body :deep(.map-container),
  .shared-schedule-map-body :deep(.map) {
    min-height: 0;
  }

  .shared-schedule-map-body {
    flex: 1;
  }

  .shared-schedule-map-body :deep(.map-container),
  .shared-schedule-map-body :deep(.map) {
    height: 100%;
  }
}

.schedule-tabs {
  width: 100%;
}

.tabs-header {
  display: flex;
  gap: 0.5rem;
  border-bottom: 2px solid #e0e0e0;
  margin-bottom: 1.5rem;
  overflow-x: auto;
  scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;
}

.tabs-header::-webkit-scrollbar {
  height: 4px;
}

.tabs-header::-webkit-scrollbar-thumb {
  background: var(--primary-color, #b4cf9e);
  border-radius: 4px;
}

.tab-button {
  padding: 0.75rem 1.5rem;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  cursor: pointer;
  font-weight: 600;
  color: #666;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 100px;
  flex: 1 0 auto;
  white-space: nowrap;
}

.tab-button:hover {
  color: #b4cf9e;
  background-color: #f5f5f5;
}

.tab-button.active {
  color: #b4cf9e;
  border-bottom-color: #b4cf9e;
}

.tab-count {
  background-color: #b4cf9e;
  color: white;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
}

.tabs-content {
  width: 100%;
}

.day-content {
  width: 100%;
}

.day-header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: linear-gradient(135deg, #b4cf9e 0%, #c5dcb3 100%);
  border-radius: 12px;
  color: white;
}

.day-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
}

.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary {
  background-color: var(--color-surface);
  color: #b4cf9e;
}

.btn-primary:hover {
  background-color: #f5f5f5;
  transform: translateY(-2px);
}

.btn-sm {
  padding: 0.4rem 0.8rem;
  font-size: 0.9rem;
}

.no-items {
  text-align: center;
  padding: 3rem;
  color: #999;
  font-size: 1.1rem;
}

.schedule-items {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.day-map-section {
  background: var(--color-surface);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-lg);
  padding: 1rem;
  box-shadow: var(--shadow-sm);
}

.day-map-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 0.75rem;
  color: var(--color-text);
  font-size: 0.95rem;
  font-weight: 700;
}

.day-map-section :deep(.map) {
  min-height: 280px;
}

.schedule-item {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  background: #f9f9f9;
  border-radius: 12px;
  border-left: 4px solid #b4cf9e;
  transition: all 0.2s;
}

.schedule-item:hover {
  background: #f5f5f5;
  transform: translateX(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.item-pin-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 30px;
  width: 30px;
  height: 30px;
  color: #ffffff;
  background: #b4cf9e;
  border: 2px solid #ffffff;
  border-radius: 999px;
  box-shadow: 0 4px 10px rgba(180, 207, 158, 0.28);
  font-size: 0.82rem;
  font-weight: 800;
  line-height: 1;
}

.item-icon {
  flex-shrink: 0;
}

.category-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: var(--color-surface);
  border-radius: 50%;
  color: #b4cf9e;
}

.item-time {
  width: 128px;
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
}

.time-badge {
  box-sizing: border-box;
  width: 128px;
  min-width: 128px;
  min-height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0.45rem 0.6rem;
  background: var(--color-surface);
  border-radius: 8px;
  font-weight: 600;
  color: #b4cf9e;
  font-size: 0.9rem;
  white-space: nowrap;
}

.item-content {
  flex: 1;
  min-width: 0;
}

.item-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.item-title-row .item-title {
  margin: 0;
}

.members-avatars {
  display: flex;
  flex-shrink: 0;
  justify-content: flex-end;
  padding-left: 4px;
}

.member-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid white;
  overflow: hidden;
  margin-left: -4px;
}

.member-avatar:first-child {
  margin-left: 0;
}

.member-avatar .avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.member-avatar .avatar-placeholder {
  width: 100%;
  height: 100%;
  background: var(--bg-tertiary, #e5e7eb);
  color: var(--text-tertiary, #9ca3af);
}

.item-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  color: #333;
}

.item-description {
  margin: 0.5rem 0;
  color: #666;
  line-height: 1.6;
}

.item-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.75rem;
  gap: 1rem;
  flex-wrap: wrap;
}

.item-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.meta-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.75rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
}

.reservation-tag {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.reservation-pending-tag {
  background-color: #fff3e0;
  color: #e65100;
}

.not-needed-tag {
  background-color: #f5f5f5;
  color: #757575;
}

.location-tag {
  background-color: #e3f2fd;
  color: #1976d2;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cost-tag {
  background-color: #fff3e0;
  color: #f57c00;
}

.item-actions {
  display: flex;
  gap: 0.5rem;
  margin-left: auto;
}

.action-btn {
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-weight: 600;
  font-size: 0.9rem;
}

.edit-btn {
  background-color: #e3f2fd;
  color: #1976d2;
}

.edit-btn:hover {
  background-color: #bbdefb;
  transform: translateY(-2px);
}

.delete-btn {
  background-color: #ffebee;
  color: #c62828;
}

.delete-btn:hover {
  background-color: #ffcdd2;
  transform: translateY(-2px);
}

.section-title {
  margin: 0 0 1.5rem 0;
  font-size: 1.5rem;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.section-subtitle {
  margin: 0 0 1rem 0;
  font-size: 1.2rem;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.overview-container,
.checklist-container,
.note-container,
.warikan-container {
  padding: 1rem;
}

.overview-section {
  margin-bottom: 2rem;
}

.info-card {
  background: #f9f9f9;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  border-left: 4px solid #b4cf9e;
}

.info-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.info-header h4 {
  margin: 0;
  font-size: 1.1rem;
  color: #333;
  font-weight: 700;
}

.info-icon {
  color: #b4cf9e;
}

.info-content {
  margin-top: 0.75rem;
}

.info-value {
  font-size: 1.1rem;
  color: #333;
  font-weight: 600;
  line-height: 1.6;
}

.info-value.large {
  font-size: 1.8rem;
  color: #b4cf9e;
}

.members-list-overview {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.member-chip {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--color-surface);
  border-radius: 20px;
  font-weight: 600;
  color: #333;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.member-avatar-small {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #b4cf9e 0%, #c5dcb3 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
}

.schedule-summary,
.checklist-summary {
  background: #f9f9f9;
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e0e0e0;
}

.summary-item:last-child {
  border-bottom: none;
}

.summary-label {
  color: #666;
  font-size: 0.95rem;
  font-weight: 500;
}

.summary-value {
  color: #333;
  font-size: 1.1rem;
  font-weight: 700;
}

.checklist-container,
.note-container,
.warikan-container {
  padding: 1rem;
}

.checklist-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.checklist-category {
  background: #f9f9f9;
  border-radius: 12px;
  padding: 1.5rem;
}

.category-title {
  margin: 0 0 1rem 0;
  font-size: 1.2rem;
  color: #b4cf9e;
  font-weight: 700;
}

.checklist-header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.checklist-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: var(--color-surface);
  border-radius: 8px;
  margin-bottom: 0.5rem;
}

.checklist-item.completed .checklist-name {
  text-decoration: line-through;
  color: #888;
}

.checklist-item-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
}

.checklist-checkbox {
  width: 1.15rem;
  height: 1.15rem;
  cursor: pointer;
}

.checklist-checkbox:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.checklist-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.checklist-title-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.priority-badge {
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  white-space: nowrap;
}

.priority-high {
  background-color: #fee2e2;
  color: var(--color-danger);
}

.priority-normal {
  background-color: #fef3c7;
  color: #d97706;
}

.priority-low {
  background-color: #d1fae5;
  color: #059669;
}

.copy-btn {
  background-color: #f3e8ff;
  color: #7e22ce;
}

.copy-btn:hover {
  background-color: #e9d5ff;
  transform: translateY(-2px);
}

.checklist-name {
  font-weight: 600;
  color: #333;
}

.checklist-deadline {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  color: #666;
  font-size: 0.9rem;
}

.checklist-memo {
  margin: 0.25rem 0 0 0;
  font-size: 0.85rem;
  color: #666;
}

.checklist-item-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: var(--color-surface);
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  padding: 1.5rem;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
}

.modal-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.form-group label {
  font-weight: 600;
  font-size: 0.9rem;
  color: #333;
}

.form-group input,
.form-group textarea {
  padding: 0.6rem;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 0.95rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
}

.empty-state {
  text-align: center;
  padding: 3rem;
  color: #999;
  font-size: 1.1rem;
}

.member-summaries {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.member-summary-card {
  background: #f9f9f9;
  border-radius: 12px;
  padding: 1.5rem;
  border-left: 4px solid #b4cf9e;
}

.member-summary-card.balance-positive {
  border-left-color: #4caf50;
}

.member-summary-card.balance-negative {
  border-left-color: #f44336;
}

.member-summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.member-summary-header h4 {
  margin: 0;
  font-size: 1.1rem;
  color: #333;
}

.balance-badge {
  padding: 0.4rem 0.8rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.95rem;
}

.balance-badge.positive {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.balance-badge.negative {
  background-color: #ffebee;
  color: #c62828;
}

.member-summary-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
  color: #666;
}

.paid-amount,
.owed-amount {
  font-weight: 600;
  color: #333;
}

.floating-btn {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #b4cf9e 0%, #c5dcb3 100%);
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  z-index: 50;
}

.floating-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
}

.floating-btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 768px) {
  .shiori-content {
    padding: 1rem;
  }

  .trip-overview {
    padding: 1.5rem;
  }

  .overview-title {
    font-size: 1.5rem;
  }

  .schedule-item {
    flex-direction: column;
    gap: 0.75rem;
  }

  .member-summaries {
    grid-template-columns: 1fr;
  }

  .info-item {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
}

/* ===== 割り勘タブ ===== */
.warikan-tab-content {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 400px;
  border-radius: 12px;
}

.warikan-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem 1rem;
  box-sizing: border-box;
}

.warikan-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.stats-panel {
  background: var(--color-surface);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.stats-panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.stats-panel-title {
  display: grid;
  gap: 0.35rem;
}

.stats-panel-title .section-title {
  margin: 0;
}

.stats-panel-summary {
  margin: 0;
  color: #64748b;
  font-size: 0.9rem;
}

.stats-toggle-button {
  border: 1px solid #d8e3d1;
  border-radius: 999px;
  background: #f8fafc;
  color: #37512d;
  cursor: pointer;
  font-weight: 700;
  padding: 0.45rem 0.9rem;
}

.budget-section {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(280px, 0.9fr);
  gap: 1rem;
}

.budget-summary-card,
.expense-chart-card {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #f8fafc;
  padding: 1rem;
}

.budget-summary-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.budget-card-title {
  margin: 0;
  color: var(--color-text);
  font-size: 1rem;
}

.budget-status {
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  background: #dcfce7;
  color: #166534;
  font-size: 0.78rem;
  font-weight: 800;
}

.budget-status.over {
  background: #fee2e2;
  color: #b91c1c;
}

.budget-status.unset {
  background: #e5e7eb;
  color: var(--color-text-muted);
}

.budget-metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 0.75rem;
}

.budget-metric {
  display: grid;
  gap: 0.25rem;
  padding: 0.75rem;
  background: var(--color-surface);
  border-radius: 12px;
}

.metric-label {
  color: #64748b;
  font-size: 0.78rem;
}

.budget-metric strong {
  color: var(--color-text);
  font-size: 1rem;
}

.budget-metric strong.danger {
  color: var(--color-danger);
}

.budget-progress {
  height: 10px;
  margin-top: 1rem;
  overflow: hidden;
  border-radius: 999px;
  background: #e5e7eb;
}

.budget-progress-fill {
  height: 100%;
  border-radius: inherit;
  background: #22c55e;
  transition: width 0.2s ease;
}

.budget-progress-fill.over {
  background: #ef4444;
}

.expense-chart-card {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 1rem;
  align-items: center;
}

.expense-donut {
  width: 132px;
  height: 132px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  position: relative;
}

.expense-donut::after {
  content: "";
  position: absolute;
  inset: 22px;
  background: var(--color-surface);
  border-radius: 50%;
}

.expense-donut-center {
  position: relative;
  z-index: 1;
  display: grid;
  gap: 0.2rem;
  text-align: center;
}

.expense-donut-center span {
  color: #64748b;
  font-size: 0.72rem;
}

.expense-donut-center strong {
  color: var(--color-text);
  font-size: 0.9rem;
}

.expense-breakdown {
  display: grid;
  gap: 0.7rem;
}

.expense-breakdown-row {
  display: grid;
  gap: 0.35rem;
}

.breakdown-label-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 0.5rem;
  align-items: center;
  color: var(--color-text);
  font-size: 0.86rem;
}

.breakdown-color {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.breakdown-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.breakdown-bar {
  height: 7px;
  overflow: hidden;
  border-radius: 999px;
  background: #e5e7eb;
}

.breakdown-bar-fill {
  height: 100%;
  border-radius: inherit;
}

.no-chart-data {
  margin: 0;
  color: #64748b;
  font-size: 0.86rem;
}

@media (max-width: 768px) {
  .stats-panel-header,
  .budget-summary-header {
    align-items: stretch;
    flex-direction: column;
  }

  .budget-section,
  .expense-chart-card {
    grid-template-columns: 1fr;
  }

  .expense-donut {
    justify-self: center;
  }
}

.summary-section,
.settlements-section,
.expenses-section {
  background: var(--color-surface);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.wk-member-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.wk-member-card {
  background: #f8fafc;
  border-radius: 12px;
  padding: 1.25rem;
  border: 1px solid #e2e8f0;
  transition: all 0.2s;
}

.wk-member-card.balance-positive {
  border-left: 4px solid #10b981;
}

.wk-member-card.balance-negative {
  border-left: 4px solid #ef4444;
}

.wk-member-card.balance-zero {
  border-left: 4px solid #94a3b8;
}

.wk-member-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.wk-member-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.wk-member-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #b4cf9e 0%, #c5dcb3 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  overflow: hidden;
  flex-shrink: 0;
}

.wk-member-avatar .avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.wk-member-avatar .avatar-placeholder {
  color: white;
}

.wk-member-name {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
}

.wk-balance-badge {
  padding: 0.35rem 0.75rem;
  border-radius: 20px;
  font-weight: 700;
  font-size: 0.9rem;
}

.wk-balance-badge.positive {
  background: #d1fae5;
  color: #065f46;
}

.wk-balance-badge.negative {
  background: #fee2e2;
  color: #991b1b;
}

.wk-member-details {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.wk-detail-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.875rem;
}

.wk-detail-label {
  color: #64748b;
}

.wk-detail-value.paid {
  font-weight: 600;
  color: #10b981;
}

.wk-detail-value.owed {
  font-weight: 600;
  color: var(--color-danger);
}

/* 精算リスト */
.settlement-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
}

.settlement-item {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.75rem;
  width: 100%;
  min-width: 0;
  padding: 1rem;
  box-sizing: border-box;
  border: 1px solid var(--color-border-light);
  border-radius: 12px;
  background: var(--color-surface);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;
}

.settlement-item:hover {
  transform: translateX(4px);
}

.settlement-content {
  display: flex;
  width: 100%;
  min-width: 0;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
}

.settlement-content .from-member,
.settlement-content .to-member {
  min-width: 0;
  overflow-wrap: anywhere;
}

.settlement-content .from-member {
  color: var(--color-danger);
}

.arrow {
  color: #94a3b8;
  font-size: 1.2rem;
}

.settlement-content .to-member {
  color: #10b981;
}

.settlement-amount {
  min-width: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
  overflow-wrap: anywhere;
}

.no-settlements {
  text-align: center;
  padding: 2.5rem;
  color: #10b981;
}

.balance-status {
  align-self: flex-start;
  margin-bottom: 0.45rem;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  background: #eef2f7;
  color: #475569;
  font-size: 0.75rem;
  font-weight: 700;
}

.balance-status.receive { background: #dcfce7; color: #166534; }
.balance-status.pay { background: #fee2e2; color: #991b1b; }

.calculation-warning {
  padding: 1rem 1.25rem;
  border: 1px solid #f59e0b;
  border-left-width: 5px;
  border-radius: 12px;
  background: #fffbeb;
  color: #78350f;
}

.calculation-warning h2 { margin: 0 0 0.35rem; font-size: 1rem; }
.calculation-warning p { margin: 0 0 0.5rem; }
.calculation-warning ul { margin: 0; padding-left: 1.25rem; }

.success-icon {
  margin-bottom: 1rem;
  color: #10b981;
}

/* 費用詳細 */
.expenses-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.expenses-heading .section-title {
  margin-bottom: 0;
}

.shared-warikan-add {
  background: #b4cf9e;
  color: white;
}

.shared-warikan-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
}

.expense-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
}

.expense-item {
  background: #f8fafc;
  border-radius: 12px;
  padding: 1.25rem;
  border: 1px solid #e2e8f0;
}

.expense-item.warikan-item {
  border-left: 4px solid #f59e0b;
}

.expense-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
  gap: 1rem;
}

.expense-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #1e293b;
  flex-wrap: wrap;
}

.expense-icon {
  color: #b4cf9e;
  flex-shrink: 0;
}

.expense-badge {
  padding: 0.2rem 0.6rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
}

.expense-badge.schedule {
  background: #dbeafe;
  color: #1d4ed8;
}

.expense-badge.independent {
  background: #fef3c7;
  color: #92400e;
}

.expense-cost {
  font-weight: 700;
  font-size: 1.1rem;
  color: #1e293b;
  white-space: nowrap;
}

.expense-details {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.4rem;
}

.expense-info {
  display: flex;
  gap: 0.35rem;
  font-size: 0.875rem;
  color: #475569;
}

.expense-info-label {
  font-weight: 600;
  color: #64748b;
  flex-shrink: 0;
}

.payer-name {
  font-weight: 600;
  color: #b4cf9e;
}

.payer-chip {
  display: inline-flex;
  width: fit-content;
  max-width: 100%;
  gap: 0.35rem;
  align-items: center;
  color: #4f7f3f;
  font-weight: 700;
}

.payer-icon {
  display: inline-flex;
  width: 1.35rem;
  height: 1.35rem;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #eef7e7;
  color: #4f7f3f;
}

.no-expenses {
  text-align: center;
  padding: 2rem;
  color: #94a3b8;
}

.empty-icon {
  margin-bottom: 1rem;
  color: #94a3b8;
}

.empty-title {
  margin: 0 0 0.5rem 0;
  color: #475569;
}

.empty-description {
  color: #94a3b8;
}

/* 通常のしおり画面と共通のトーン・タイポグラフィ */
.shared-shiori-view {
  color: var(--color-text);
  font-size: 0.9375rem;
}

.shiori-header {
  background: linear-gradient(
    135deg,
    var(--shiori-light, #b4cf9e) 0%,
    var(--shiori-lighter, #c5dcb3) 100%
  );
  padding: 1.5rem 0.75rem;
  min-height: 150px;
}

.shiori-title {
  font-size: 1.25rem;
  margin-bottom: 0.75rem;
}

.shiori-meta {
  font-size: 0.8125rem;
}

.tabs-wrapper {
  position: relative;
  top: 0;
  z-index: 95;
}

.tab-content {
  padding: 1rem 0.75rem;
  background: var(--color-background);
}

.section-title {
  color: var(--color-text);
  font-size: 1.25rem;
  font-weight: 700;
}

.shared-filter-action {
  position: relative;
  margin-bottom: 1rem;
}

.shared-filter-toggle {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  box-shadow: var(--shadow-sm);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}

.filter-active-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--primary-color);
}

.shared-filter-overlay {
  position: fixed;
  z-index: 200;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
}

.shared-filter-sidebar {
  position: fixed;
  z-index: 201;
  top: 0;
  left: -100%;
  display: flex;
  width: min(320px, 85vw);
  height: 100dvh;
  overflow-y: auto;
  flex-direction: column;
  background: var(--color-surface);
  box-shadow: var(--shadow-xl);
  transition: left 0.3s ease;
}

.shared-filter-sidebar.is-open {
  left: 0;
}

.shared-filter-sidebar-header {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--color-border-light);
  background: linear-gradient(135deg, #b4cf9e 0%, #c5dcb3 100%);
  color: white;
  font-size: 1rem;
}

.shared-filter-sidebar-header button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: var(--radius-md);
  background: rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 1.25rem;
  cursor: pointer;
}

.shared-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.75rem;
  padding: 1rem;
  margin-bottom: 1rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}

.shared-filter-group {
  display: flex;
  flex: 1 1 150px;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 130px;
}

.shared-filter-group > span {
  color: var(--color-text-muted);
  font-size: 0.8125rem;
  font-weight: 600;
}

.filter-select {
  min-height: 38px;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  font-size: 0.875rem;
}

.filter-select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(180, 207, 158, 0.15);
}

.filter-reset-button {
  min-height: 38px;
  padding: 0.5rem 0.875rem;
  border: none;
  border-radius: var(--radius-md);
  background: #ef4444;
  color: white;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
}

.settlement-item.settled {
  opacity: 0.72;
  background: #f8fafc;
}

.settlement-item.settled .from-member,
.settlement-item.settled .to-member,
.settlement-item.settled .arrow,
.settlement-item.settled .settlement-amount {
  text-decoration: line-through;
}

.status-badge {
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
}

.status-badge.unpaid,
.expense-badge.unsettled {
  color: #92400e;
  background: #fef3c7;
}

.status-badge.paid,
.expense-badge.settled {
  color: #166534;
  background: #dcfce7;
}

.settlement-controls {
  display: flex;
  width: 100%;
  min-width: 0;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.status-toggle,
.expense-settlement-button {
  max-width: 100%;
  padding: 0.45rem 0.7rem;
  border-radius: var(--radius-md);
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  white-space: normal;
}

.status-toggle-complete,
.expense-settlement-button.settlement-complete {
  border: 1px solid #86efac;
  background: #f0fdf4;
  color: #166534;
}

.status-toggle-undo,
.expense-settlement-button.settlement-undo {
  border: 1px solid #fed7aa;
  background: #fff7ed;
  color: #9a3412;
}

.status-toggle:disabled,
.expense-settlement-button:disabled {
  opacity: 0.55;
  cursor: wait;
}

.expense-settlement-button {
  display: block;
  margin: 0.75rem 0 0 auto;
}

/* スケジュール表示を通常画面と一致させる */
.schedule-tabs {
  background: var(--color-surface);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-md);
  overflow: hidden;
}

.tabs-content {
  padding: 1.5rem;
}

.day-header-bar {
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: var(--radius-lg);
}

.day-header-bar .day-title {
  flex: 1;
  min-width: 0;
  margin: 0;
  color: white;
  font-size: 1rem;
  font-weight: 700;
}

.day-header-bar .btn-primary {
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.25);
  color: white;
}

.no-items {
  padding: 3rem 1rem;
  border-radius: var(--radius-md);
  background: var(--color-background);
  color: var(--color-text-muted);
  font-size: 0.875rem;
}

.schedule-item {
  padding: 1.25rem;
  background: var(--color-background);
  border-left-color: var(--primary-color);
  border-radius: var(--radius-lg);
  transition: all var(--transition-base);
}

.schedule-item:hover {
  background: var(--color-surface);
  box-shadow: var(--shadow-md);
}

.category-icon {
  color: var(--primary-color);
}

.time-badge {
  padding: 0.5rem 0.625rem;
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  color: var(--color-text);
  font-size: 0.8125rem;
}

.item-title {
  margin: 0 0 0.5rem;
  color: var(--color-text);
  font-size: 1rem;
  font-weight: 700;
}

.item-description {
  margin: 0 0 0.75rem;
  color: var(--color-text-muted);
  font-size: 0.875rem;
  line-height: 1.5;
}

.meta-tag {
  padding: 0.375rem 0.75rem;
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  font-size: 0.8125rem;
  font-weight: 500;
}

.map-link {
  color: var(--primary-color);
  text-decoration: none;
}

.map-link:hover {
  border-color: var(--primary-color);
  background: var(--primary-lightest);
}

/* チェックリスト・割り勘も通常画面の文字階層へ統一 */
.checklist-container .section-title,
.warikan-container .section-title {
  color: var(--color-text);
  font-size: 1.25rem;
  font-weight: 700;
}

.category-title {
  color: var(--primary-color);
  font-size: 1rem;
}

.checklist-name {
  color: var(--color-text);
  font-size: 0.9375rem;
}

.checklist-deadline {
  color: var(--color-text-muted);
  font-size: 0.8125rem;
}

.wk-member-name,
.expense-title {
  color: var(--color-text);
  font-size: 1.125rem;
}

.expense-cost {
  color: #2563eb;
  font-size: 1.25rem;
}

.expense-info {
  color: var(--color-text-muted);
  font-size: 0.875rem;
}

.expense-info-label {
  color: var(--color-text-muted);
}

@media (min-width: 768px) {
  .overview-tab .info-item {
    grid-template-columns: 140px 1fr;
    gap: 0.75rem;
  }

  .settlement-item {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 1.25rem;
  }

  .settlement-content {
    width: auto;
    gap: 1rem;
    font-size: 1.125rem;
  }

  .settlement-controls {
    width: auto;
    flex-wrap: nowrap;
  }

  .settlement-amount {
    font-size: 1.25rem;
  }

  .shared-filter-action {
    margin-bottom: 0;
  }

  .shared-filter-toggle,
  .shared-filter-overlay {
    display: none !important;
  }

  .shared-filter-sidebar {
    position: static;
    display: block;
    width: auto;
    height: auto;
    overflow: visible;
    background: transparent;
    box-shadow: none;
  }

  .shared-filter-sidebar-header {
    display: none;
  }

  .shiori-header {
    padding: 2rem 1rem;
    min-height: 200px;
  }

  .shiori-title {
    font-size: 2rem;
  }

  .shiori-meta {
    font-size: 0.9375rem;
  }

  .tab-content {
    padding: 2rem 1rem;
  }

  .day-header-bar .day-title {
    font-size: 1.125rem;
  }
}

@media (max-width: 640px) {
  .overview-tab > .card {
    padding: 0.875rem;
    border-radius: var(--radius-md);
  }

  .overview-tab .card-header {
    margin: -0.875rem -0.875rem 0.875rem;
    padding: 0.75rem 0.875rem;
    border-radius: var(--radius-md) var(--radius-md) 0 0;
  }

  .overview-tab .card-title {
    font-size: 1rem;
  }

  .overview-tab .info-grid,
  .overview-tab .members-list {
    gap: 0.625rem;
  }

  .overview-tab .info-label,
  .overview-tab .info-value {
    font-size: 0.75rem;
  }

  .warikan-container {
    padding: 0.75rem 0;
  }

  .warikan-content {
    gap: 1rem;
  }

  .summary-section,
  .settlements-section,
  .expenses-section {
    min-width: 0;
    padding: 0.75rem;
    box-sizing: border-box;
  }

  .settlement-item {
    padding: 0.75rem;
    border-radius: var(--radius-md);
  }

  .settlement-list {
    gap: 0.5rem;
  }

  .status-toggle {
    padding: 0.35rem 0.5rem;
    font-size: 0.6875rem;
  }

  .shared-filters {
    display: flex;
    gap: 0.75rem;
    padding: 1rem;
    margin: 0;
    border: none;
    border-radius: 0;
    box-shadow: none;
  }

  .shared-filter-group {
    min-width: 0;
  }

  .filter-reset-button {
    width: 100%;
  }

  .tabs-content,
  .day-content {
    padding: 0.75rem;
  }

  .day-header-bar {
    margin-bottom: 0.75rem;
    padding: 0.625rem 0.75rem;
  }

  .day-header-bar .day-title {
    color: white;
    font-size: 1rem;
  }

  .schedule-items {
    gap: 0.5rem;
  }

  .schedule-item {
    gap: 0.5rem;
    padding: 0.75rem;
    border-left-width: 3px;
    border-radius: var(--radius-md);
  }

  .time-badge {
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
  }

  .item-title {
    font-size: 0.875rem;
    line-height: 1.35;
  }

  .item-description {
    margin-bottom: 0.5rem;
    font-size: 0.75rem;
    line-height: 1.4;
  }

  .meta-tag {
    gap: 0.25rem;
    padding: 0.25rem 0.5rem;
    font-size: 0.6875rem;
  }

  .checklist-container .section-title,
  .warikan-container .section-title {
    font-size: 1.125rem;
  }

  .wk-member-name,
  .expense-title {
    font-size: 0.9375rem;
  }

  .expense-cost {
    font-size: 1rem;
  }

  .expense-info {
    font-size: 0.75rem;
  }
}

.overview-layout {
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
}

@media (max-width: 992px) {
  .overview-layout {
    flex-direction: column;
  }
}

.overview-main {
  flex: 1;
  min-width: 0;
}

.overview-side {
  width: 380px;
  flex-shrink: 0;
}

@media (max-width: 992px) {
  .overview-side {
    width: 100%;
  }
}
</style>
